import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useWebSocket } from '../contexts/WebSocketContext';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WinnersPodium } from '../components/WinnersPodium';
import { Calendar, Users, Trophy, ArrowRight, Clock, UserCheck, UserX, Loader, Edit, Save, X, Camera, Image, Trash2, ZoomIn, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner, CardSkeleton } from '@/components/ui/LoadingSpinner';
import TournamentBracketInteractive from '../components/TournamentBracketInteractive';
import LiveResultsFeed from '../components/LiveResultsFeed';
import TournamentReplay from '../components/TournamentReplay';
import SponsorBanner from '../components/SponsorBanner';
import CancellationPolicyCard from '../components/CancellationPolicyCard';
import { getGoogleCalendarUrl, downloadIcs } from '../utils/calendarUtils';
import { CalendarPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const TournamentDetailsPage = () => {
  const { id } = useParams();
  const { API, user } = useContext(AuthContext);
  const { lastMessage } = useWebSocket();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [waitingUsers, setWaitingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('players');
  const [allUsers, setAllUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showReplay, setShowReplay] = useState(false);
  const [isEditGroupsModalOpen, setIsEditGroupsModalOpen] = useState(false);
  const [selectedPlayerToAdd, setSelectedPlayerToAdd] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const galleryInputRef = useRef(null);
  const [showPartnerInput, setShowPartnerInput] = useState(false);
  const [isEditTournamentModalOpen, setIsEditTournamentModalOpen] = useState(false);
  const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState(false);
  const [addPlayerTab, setAddPlayerTab] = useState('registered'); // 'registered' or 'external'
  const [externalPlayerName, setExternalPlayerName] = useState('');
  const [externalPlayerPhone, setExternalPlayerPhone] = useState('');
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [paymentWaLinks, setPaymentWaLinks] = useState(null);
  const [loadingPaymentWa, setLoadingPaymentWa] = useState(false);
  const [paymentOnlyUnpaid, setPaymentOnlyUnpaid] = useState(true);
  const [waitlistWaLinks, setWaitlistWaLinks] = useState(null);
  const [loadingWaitlistWa, setLoadingWaitlistWa] = useState(false);
  // State for doubles pair add
  const [pairPlayer1Type, setPairPlayer1Type] = useState('registered'); // 'registered' or 'external'
  const [pairPlayer1Id, setPairPlayer1Id] = useState('');
  const [pairPlayer1Name, setPairPlayer1Name] = useState('');
  const [pairPlayer1Phone, setPairPlayer1Phone] = useState('');
  const [pairPlayer2Type, setPairPlayer2Type] = useState('external'); // 'registered' or 'external'
  const [pairPlayer2Id, setPairPlayer2Id] = useState('');
  const [pairPlayer2Name, setPairPlayer2Name] = useState('');
  const [pairPlayer2Phone, setPairPlayer2Phone] = useState('');
  const [pairSearchQuery1, setPairSearchQuery1] = useState('');
  const [pairSearchQuery2, setPairSearchQuery2] = useState('');
  
  // Group swap mode state
  const [swapMode, setSwapMode] = useState(false);
  const [swapSelection, setSwapSelection] = useState(null); // { playerId, groupName, playerName }
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [pointsPreview, setPointsPreview] = useState(null);
  const [awardEdits, setAwardEdits] = useState({});
  const [awarding, setAwarding] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // הערכת דמי הביטול בצד הלקוח לפי הזמן שנותר עד התחרות
  const getEstimatedCancelFee = () => {
    if (!tournament) return { fee: 0, hours: null };
    try {
      let start;
      if (tournament.date) {
        const datePart = tournament.date.split('T')[0];
        const time = tournament.start_time || '09:00';
        // שעון ישראל UTC+3 → המרה ל-UTC
        start = new Date(`${datePart}T${time}:00+03:00`);
      }
      if (!start || isNaN(start.getTime())) return { fee: 0, hours: null };
      const hours = (start.getTime() - Date.now()) / 3600000;
      let fee = 0;
      if (hours >= 48) fee = 0;
      else if (hours >= 24) fee = 50;
      else if (hours >= 12) fee = 100;
      else fee = 160;
      return { fee, hours: Math.round(hours * 10) / 10 };
    } catch {
      return { fee: 0, hours: null };
    }
  };

  const openPointsDialog = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/tournaments/${id}/points-preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPointsPreview(res.data);
      const edits = {};
      res.data.players.forEach(p => {
        edits[p.user_id] = p.previously_awarded > 0 ? p.previously_awarded : p.suggested_points;
      });
      setAwardEdits(edits);
      setShowPointsDialog(true);
    } catch (e) {
      toast.error('שגיאה בחישוב הנקודות');
    }
  };

  const handleAwardPoints = async () => {
    if (!pointsPreview) return;
    setAwarding(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const awards = pointsPreview.players.map(p => ({
        user_id: p.user_id,
        points: parseInt(awardEdits[p.user_id], 10) || 0,
      }));
      const res = await axios.post(
        `${API}/admin/tournaments/${id}/award-points`,
        { awards },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`נקודות חולקו ל-${res.data.players_updated} שחקנים! 🎉`);
      setShowPointsDialog(false);
      fetchTournamentDetails();
    } catch (e) {
      toast.error('שגיאה בחלוקת הנקודות');
    } finally {
      setAwarding(false);
    }
  };

  const [checkInStatus, setCheckInStatus] = useState(null);

  const fetchCheckInStatus = async () => {
    if (!user) { setCheckInStatus(null); return; }
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.get(`${API}/tournaments/${id}/check-in-status`, { headers: { Authorization: `Bearer ${token}` } });
      setCheckInStatus(res.data);
    } catch { setCheckInStatus(null); }
  };

  const handleSelfCheckIn = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API}/tournaments/${id}/check-in`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("צ'ק-אין בוצע בהצלחה! 🎾");
      fetchCheckInStatus();
      fetchTournamentDetails();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאה בצ'ק-אין");
    }
  };

  useEffect(() => {
    if (tournament && user) fetchCheckInStatus();
  }, [tournament?.status, tournament?.attended_players, user?.id, id]);
  const [movingPlayer, setMovingPlayer] = useState(false);
  
  // בדיקה אם המשתמש הוא אדמין
  const isAdmin = user?.is_admin || ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email);

  // יצירת קישורי WhatsApp לתשלום (Paybox) לשחקנים הרשומים
  const fetchPaymentWhatsapp = async () => {
    setLoadingPaymentWa(true);
    try {
      const res = await axios.get(`${API}/admin/tournaments/${tournament.id}/payment-whatsapp`);
      setPaymentWaLinks(res.data.targets || []);
      if ((res.data.targets || []).length === 0) {
        toast.info('אין שחקנים רשומים עם מספר טלפון');
      }
    } catch (e) {
      console.error('payment whatsapp error', e);
      toast.error('שגיאה ביצירת קישורי תשלום');
    } finally {
      setLoadingPaymentWa(false);
    }
  };

  // יצירת קישורי WhatsApp לרשימת המתנה (התפנה מקום)
  const fetchWaitlistWhatsapp = async () => {
    setLoadingWaitlistWa(true);
    try {
      const res = await axios.get(`${API}/admin/tournaments/${tournament.id}/waitlist-whatsapp`);
      setWaitlistWaLinks(res.data.targets || []);
      if ((res.data.targets || []).length === 0) {
        toast.info('אין שחקנים ברשימת המתנה עם מספר טלפון');
      }
    } catch (e) {
      console.error('waitlist whatsapp error', e);
      toast.error('שגיאה ביצירת קישורי רשימת המתנה');
    } finally {
      setLoadingWaitlistWa(false);
    }
  };
  
  const [editFormData, setEditFormData] = useState({
    name: '',
    date: '',
    location: '',
    description: '',
    start_time: '',
    max_players: '',
    whatsapp_link: '',
    prizes: {
      first_place: '',
      second_place: '',
      third_place: '',
      consolation_winner: ''
    }
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [isEditRulesModalOpen, setIsEditRulesModalOpen] = useState(false);
  const [rulesText, setRulesText] = useState('');
  const [savingRules, setSavingRules] = useState(false);
  const [isPrizesModalOpen, setIsPrizesModalOpen] = useState(false);
  const [prizesForm, setPrizesForm] = useState({
    first_place: '',
    second_place: '',
    third_place: '',
    consolation_winner: ''
  });
  const [savingPrizes, setSavingPrizes] = useState(false);
  const currentUser = user;

  // בדיקה האם זה טורניר זוגות
  const isDoublesCategory = tournament?.category?.includes('זוגות');
  
  // פונקציה לקיבוץ שחקנים לזוגות (לטורנירי זוגות בלבד)
  const groupPlayersIntoPairs = (players) => {
    if (!isDoublesCategory || !tournament?.partnerships) {
      return players;
    }

    const pairs = [];
    const processedIds = new Set();

    players.forEach(player => {
      if (processedIds.has(player.id)) return;

      const partnerName = tournament.partnerships[player.id];
      if (partnerName) {
        // מצא את הפרטנר ברשימת השחקנים
        const partner = players.find(p => 
          p.full_name === partnerName && !processedIds.has(p.id)
        );

        if (partner) {
          // שני השחקנים נמצאו - צור זוג
          pairs.push({
            id: player.id, // שמור את ה-ID של השחקן הראשון
            player1: player,
            player2: partner,
            pairName: `${player.full_name} + ${partner.full_name}`,
            total_points: (player.total_points || 0) + (partner.total_points || 0),
            rank: Math.min(player.rank || 999, partner.rank || 999)
          });
          processedIds.add(player.id);
          processedIds.add(partner.id);
        } else {
          // רק שחקן אחד מהזוג נרשם
          pairs.push({
            id: player.id,
            player1: player,
            player2: null,
            pairName: `${player.full_name} + ${partnerName}`,
            total_points: player.total_points || 0,
            rank: player.rank || 999
          });
          processedIds.add(player.id);
        }
      } else {
        // שחקן ללא פרטנר (לא צריך להופיע בטורניר זוגות)
        processedIds.add(player.id);
      }
    });

    return pairs;
  };

  useEffect(() => {
    fetchTournamentDetails();
    fetchMatches();
    fetchGallery();
    // Polling every 2 minutes (reduced from 30s to prevent server overload)
    const interval = setInterval(() => {
      fetchTournamentDetails();
      fetchMatches();
    }, 120000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/tournaments/${id}/gallery`);
      setGallery(res.data.gallery || []);
    } catch (e) {
      setGallery([]);
    }
  };

  // האזנה לעדכוני WebSocket
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'tournament_update' && lastMessage.tournament_id === id) {
      console.log('🎾 Tournament update received:', lastMessage);
      // רענון הנתונים כשיש עדכון
      fetchTournamentDetails();
      fetchMatches();
      toast.info('התחרות עודכנה!');
    }
  }, [lastMessage, id]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both array and object with users property
      let users = [];
      if (Array.isArray(response.data)) {
        users = response.data;
      } else if (response.data && Array.isArray(response.data.users)) {
        users = response.data.users;
      }
      console.log('📋 Fetched users:', users.length);
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAllUsers([]); // Set empty array on error
      toast.error('שגיאה בטעינת רשימת משתמשים');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/tournaments/${id}/bracket`);
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      setMatches([]);
    }
  };

  // פתיחת מודל עריכת תחרות
  const openEditTournamentModal = () => {
    if (!tournament) return;
    setEditFormData({
      name: tournament.name || '',
      date: tournament.date || '',
      location: tournament.location || '',
      description: tournament.description || '',
      start_time: tournament.start_time || '',
      max_players: tournament.max_players || '',
      type: tournament.type || 'daily',
      category: tournament.category || 'דרג א',
      whatsapp_link: tournament.whatsapp_link || '',
      prizes: {
        first_place: tournament.prizes?.first_place || '',
        second_place: tournament.prizes?.second_place || '',
        third_place: tournament.prizes?.third_place || '',
        consolation_winner: tournament.prizes?.consolation_winner || ''
      }
    });
    setIsEditTournamentModalOpen(true);
  };

  // שמירת עריכת תחרות
  const saveEditTournament = async () => {
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const updateData = {
        name: editFormData.name || undefined,
        date: editFormData.date || undefined,
        location: editFormData.location || undefined,
        description: editFormData.description || undefined,
        start_time: editFormData.start_time || undefined,
        max_players: editFormData.max_players ? parseInt(editFormData.max_players) : undefined,
        type: editFormData.type || undefined,
        category: editFormData.category || undefined,
        whatsapp_link: editFormData.whatsapp_link || undefined,
        prizes: {
          first_place: editFormData.prizes.first_place || undefined,
          second_place: editFormData.prizes.second_place || undefined,
          third_place: editFormData.prizes.third_place || undefined,
          consolation_winner: editFormData.prizes.consolation_winner || undefined
        }
      };
      
      await axios.put(`${API}/admin/tournaments/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('התחרות עודכנה בהצלחה!');
      setIsEditTournamentModalOpen(false);
      fetchTournamentDetails(); // רענון הנתונים
    } catch (error) {
      console.error('Failed to update tournament:', error);
      toast.error('שגיאה בעדכון התחרות');
    } finally {
      setSavingEdit(false);
    }
  };

  // שמירת שיטת התחרות
  const saveRulesText = async () => {
    setSavingRules(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(`${API}/admin/tournaments/${id}`, {
        rules_text: rulesText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('שיטת התחרות עודכנה בהצלחה!');
      setIsEditRulesModalOpen(false);
      fetchTournamentDetails();
    } catch (error) {
      console.error('Failed to update rules:', error);
      toast.error('שגיאה בעדכון שיטת התחרות');
    } finally {
      setSavingRules(false);
    }
  };

  // פתיחת מודל עריכת שיטת התחרות
  const openEditRulesModal = () => {
    setRulesText(tournament?.rules_text || '');
    setIsEditRulesModalOpen(true);
  };

  // פרסים - פתיחה ושמירה
  const openPrizesModal = () => {
    setPrizesForm({
      first_place: tournament?.prizes?.first_place || '',
      second_place: tournament?.prizes?.second_place || '',
      third_place: tournament?.prizes?.third_place || '',
      consolation_winner: tournament?.prizes?.consolation_winner || ''
    });
    setIsPrizesModalOpen(true);
  };

  const savePrizes = async () => {
    setSavingPrizes(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(`${API}/admin/tournaments/${id}`, {
        prizes: prizesForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('הפרסים עודכנו בהצלחה!');
      setIsPrizesModalOpen(false);
      fetchTournamentDetails();
    } catch (error) {
      console.error('Failed to update prizes:', error);
      toast.error('שגיאה בעדכון הפרסים');
    } finally {
      setSavingPrizes(false);
    }
  };

  // הוספת שחקן רשום לתחרות
  const addRegisteredPlayer = async () => {
    if (!selectedPlayerToAdd) {
      toast.error('יש לבחור שחקן');
      return;
    }
    setAddingPlayer(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API}/admin/tournaments/${id}/add-player/${selectedPlayerToAdd}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('השחקן נוסף בהצלחה!');
      setSelectedPlayerToAdd('');
      setIsAddPlayerModalOpen(false);
      fetchTournamentDetails();
    } catch (error) {
      console.error('Failed to add player:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בהוספת שחקן');
    } finally {
      setAddingPlayer(false);
    }
  };

  // ========== פונקציות גלריה ==========
  
  // העלאת תמונה לגלריה
  const handleGalleryUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // בדיקת גודל קובץ (מקסימום 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('הקובץ גדול מדי. מקסימום 10MB');
      return;
    }

    // בדיקת סוג קובץ
    if (!file.type.startsWith('image/')) {
      toast.error('יש להעלות קובץ תמונה בלבד');
      return;
    }

    setUploadingImage(true);
    
    // דחיסת התמונה לפני העלאה (מקס 1280px, JPEG)
    const compressImage = (f) => new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const max = 1280;
          let { width, height } = img;
          if (width > max || height > max) {
            const ratio = Math.min(max / width, max / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      r.onerror = reject;
      r.readAsDataURL(f);
    });

    try {
      const base64String = await compressImage(file);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post(`${API}/tournaments/${id}/gallery/upload`, {
        image_data: base64String
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('התמונה נוספה לגלריה!');
      fetchGallery();
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בהעלאת התמונה');
    } finally {
      setUploadingImage(false);
    }
  };

  // ========== ניהול בתים - העברה / החלפה ==========
  const handleQuickMovePlayer = async (playerId, playerName, fromGroup, toGroup) => {
    if (fromGroup === toGroup || movingPlayer) return;
    setMovingPlayer(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/admin/tournaments/${id}/move-player-between-groups`,
        { user_id: playerId, from_group: fromGroup, to_group: toGroup },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${playerName} הועבר לבית ${toGroup}`);
      await fetchTournamentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהעברת שחקן');
    } finally {
      setMovingPlayer(false);
    }
  };
  
  const handleSwapSelect = async (playerId, playerName, groupName) => {
    if (movingPlayer) return;
    // בחירה ראשונה
    if (!swapSelection) {
      setSwapSelection({ playerId, playerName, groupName });
      return;
    }
    // ביטול בחירה אם לוחצים על אותו שחקן
    if (swapSelection.playerId === playerId) {
      setSwapSelection(null);
      return;
    }
    // אם באותו בית - לא צריך להחליף
    if (swapSelection.groupName === groupName) {
      toast.error('השחקנים באותו בית - בחר שחקן מבית אחר');
      return;
    }
    // ביצוע החלפה
    setMovingPlayer(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/admin/tournaments/${id}/swap-players-between-groups`,
        {
          user_id_1: swapSelection.playerId,
          group_1: swapSelection.groupName,
          user_id_2: playerId,
          group_2: groupName,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${swapSelection.playerName} ו-${playerName} הוחלפו`);
      setSwapSelection(null);
      await fetchTournamentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהחלפה');
    } finally {
      setMovingPlayer(false);
    }
  };



  // מחיקת תמונה מהגלריה
  const handleDeleteGalleryImage = async (imageIndex) => {
    if (!window.confirm('האם למחוק את התמונה?')) return;
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(`${API}/admin/tournaments/${id}/gallery/${imageIndex}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('התמונה נמחקה');
      fetchTournamentDetails();
      setSelectedImage(null);
    } catch (error) {
      console.error('Failed to delete image:', error);
      toast.error('שגיאה במחיקת התמונה');
    }
  };

  // הוספת שחקן חיצוני לתחרות
  const addExternalPlayer = async () => {
    if (!externalPlayerName.trim()) {
      toast.error('יש להזין שם שחקן');
      return;
    }
    setAddingPlayer(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API}/admin/tournaments/${id}/add-external-player`, {
        full_name: externalPlayerName.trim(),
        phone: externalPlayerPhone.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`השחקן ${externalPlayerName} נוסף בהצלחה!`);
      setExternalPlayerName('');
      setExternalPlayerPhone('');
      setIsAddPlayerModalOpen(false);
      fetchTournamentDetails();
    } catch (error) {
      console.error('Failed to add external player:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בהוספת שחקן חיצוני');
    } finally {
      setAddingPlayer(false);
    }
  };

  // הוספת זוג לתחרות זוגות
  const addPairToTournament = async () => {
    // Validate player 1
    if (pairPlayer1Type === 'registered' && !pairPlayer1Id) {
      toast.error('יש לבחור שחקן 1 מהרשימה');
      return;
    }
    if (pairPlayer1Type === 'external' && !pairPlayer1Name.trim()) {
      toast.error('יש להזין שם לשחקן 1');
      return;
    }
    // Validate player 2
    if (pairPlayer2Type === 'registered' && !pairPlayer2Id) {
      toast.error('יש לבחור שחקן 2 מהרשימה');
      return;
    }
    if (pairPlayer2Type === 'external' && !pairPlayer2Name.trim()) {
      toast.error('יש להזין שם לשחקן 2');
      return;
    }
    
    setAddingPlayer(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const payload = {
        player1: pairPlayer1Type === 'registered'
          ? { type: 'registered', player_id: pairPlayer1Id }
          : { type: 'external', full_name: pairPlayer1Name.trim(), phone: pairPlayer1Phone.trim() || null },
        player2: pairPlayer2Type === 'registered'
          ? { type: 'registered', player_id: pairPlayer2Id }
          : { type: 'external', full_name: pairPlayer2Name.trim(), phone: pairPlayer2Phone.trim() || null },
      };
      const res = await axios.post(`${API}/admin/tournaments/${id}/add-pair`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'הזוג נוסף בהצלחה!');
      // Reset state
      setPairPlayer1Id(''); setPairPlayer1Name(''); setPairPlayer1Phone('');
      setPairPlayer2Id(''); setPairPlayer2Name(''); setPairPlayer2Phone('');
      setPairPlayer1Type('registered'); setPairPlayer2Type('external');
      setPairSearchQuery1(''); setPairSearchQuery2('');
      setIsAddPlayerModalOpen(false);
      fetchTournamentDetails();
    } catch (error) {
      console.error('Failed to add pair:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בהוספת זוג');
    } finally {
      setAddingPlayer(false);
    }
  };

  
  // פונקציה למציאת מנצח התחרות
  const getTournamentWinner = () => {
    if (!matches || matches.length === 0) return null;
    
    const finalMatch = matches.find(m => m.round === 'final' && m.completed);
    if (!finalMatch || !finalMatch.winner_id) return null;
    
    // מצא את שם המנצח
    const winnerName = finalMatch.winner_id === finalMatch.player1_id 
      ? finalMatch.player1_name 
      : finalMatch.player2_name;
    
    return {
      id: finalMatch.winner_id,
      name: winnerName,
      prize: tournament?.prizes?.[tournament.type === 'daily' ? 'first_place' : 'first_place'] || ''
    };
  };
  
  // פונקציה למציאת 3 הזוכים הראשונים
  const getTopThreeWinners = () => {
    if (!matches || matches.length === 0 || tournament?.status !== 'completed') return null;
    
    const finalMatch = matches.find(m => m.round === 'final' && m.completed);
    const thirdPlaceMatch = matches.find(m => m.round === 'third_place' && m.completed);
    
    if (!finalMatch || !finalMatch.winner_id) return null;
    
    const winners = [];
    
    // מקום 1 - מנצח הגמר
    const winnerName = finalMatch.winner_id === finalMatch.player1_id 
      ? finalMatch.player1_name 
      : finalMatch.player2_name;
    
    winners.push({
      place: 1,
      id: finalMatch.winner_id,
      name: winnerName,
      prize: tournament?.prizes?.first_place || ''
    });
    
    // מקום 2 - מפסיד הגמר (חישוב מי המפסיד)
    const loserId = finalMatch.loser_id || 
                    (finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player2_id : finalMatch.player1_id);
    const loserName = finalMatch.loser_id === finalMatch.player1_id 
      ? finalMatch.player1_name 
      : (finalMatch.loser_id === finalMatch.player2_id 
          ? finalMatch.player2_name 
          : (finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player2_name : finalMatch.player1_name));
    
    if (loserId && loserName && loserId !== 'TBD') {
      winners.push({
        place: 2,
        id: loserId,
        name: loserName,
        prize: tournament?.prizes?.second_place || ''
      });
    }
    
    // מקום 3 - מנצח משחק מקום 3-4
    if (thirdPlaceMatch && thirdPlaceMatch.winner_id && thirdPlaceMatch.winner_id !== 'TBD') {
      const thirdName = thirdPlaceMatch.winner_id === thirdPlaceMatch.player1_id 
        ? thirdPlaceMatch.player1_name 
        : thirdPlaceMatch.player2_name;
      
      winners.push({
        place: 3,
        id: thirdPlaceMatch.winner_id,
        name: thirdName,
        prize: tournament?.prizes?.third_place || ''
      });
    }
    
    return winners.length > 0 ? winners : null;
  };

  const fetchTournamentDetails = async () => {
    try {
      const tournamentRes = await axios.get(`${API}/tournaments/${id}`);
      setTournament(tournamentRes.data);

      const externalPlayers = tournamentRes.data.external_players || {};
      
      // Collect all user IDs that need to be fetched
      const allUserIds = [
        ...tournamentRes.data.registered_players,
        ...(tournamentRes.data.waiting_list || [])
      ].filter(userId => userId && !userId.startsWith('manual_'));
      
      // Fetch all users in a single batch request - MUCH faster!
      let usersMap = {};
      if (allUserIds.length > 0) {
        try {
          const batchResponse = await axios.post(`${API}/users/batch`, allUserIds);
          batchResponse.data.forEach(user => {
            usersMap[user.id] = user;
          });
        } catch (err) {
          console.error('Batch fetch failed, falling back to individual requests', err);
        }
      }
      
      // Process registered players
      if (tournamentRes.data.registered_players.length > 0) {
        const registeredUsersList = tournamentRes.data.registered_players
          .map(userId => {
            if (userId.startsWith('manual_') && externalPlayers[userId]) {
              return {
                id: userId,
                full_name: externalPlayers[userId].full_name,
                phone: externalPlayers[userId].phone,
                is_external: true
              };
            }
            return usersMap[userId] || null;
          })
          .filter(u => u !== null);
        
        // חישוב דירוג לפי קטגוריה
        const usersWithCategoryRank = registeredUsersList.map(user => ({
          ...user,
          category_rank: user.rank
        }));
        
        const sortedUsers = [...usersWithCategoryRank]
          .filter(u => !u.is_external)
          .sort((a, b) => (b.total_points || 0) - (a.total_points || 0));
        
        sortedUsers.forEach((user, idx) => {
          const originalUser = usersWithCategoryRank.find(u => u.id === user.id);
          if (originalUser) {
            originalUser.category_rank = idx + 1;
          }
        });
        
        setRegisteredUsers(usersWithCategoryRank);
      }

      // Process waiting list
      if (tournamentRes.data.waiting_list && tournamentRes.data.waiting_list.length > 0) {
        const waitingUsersList = tournamentRes.data.waiting_list
          .map(userId => {
            if (userId.startsWith('manual_') && externalPlayers[userId]) {
              return {
                id: userId,
                full_name: externalPlayers[userId].full_name,
                phone: externalPlayers[userId].phone,
                is_external: true
              };
            }
            return usersMap[userId] || null;
          })
          .filter(u => u !== null);
        
        setWaitingUsers(waitingUsersList);
      }
    } catch (error) {
      console.error('Failed to fetch tournament:', error);
      toast.error('שגיאה בטעינת פרטי התחרות');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!user) {
      toast.error('עליך להיות מחובר כדי להירשם');
      navigate('/auth');
      return;
    }

    if (tournament.status === 'in_progress' || tournament.status === 'completed') {
      toast.error('התחרות כבר החלה - לא ניתן להירשם');
      return;
    }

    // בדיקה שהוזן שם פרטנר בתחרות זוגות
    if (tournament.category?.includes('זוגות') && !partnerName.trim()) {
      toast.error('יש להזין שם פרטנר לפני ההרשמה לתחרות זוגות');
      return;
    }

    try {
      const payload = { tournament_id: id };
      
      // אם זו תחרות זוגות והמשתמש הזין שם פרטנר
      if (tournament.category?.includes('זוגות') && partnerName.trim()) {
        payload.partner_name = partnerName.trim();
      }
      
      const response = await axios.post(`${API}/tournaments/register`, payload);
      
      if (response.data.status === 'waiting') {
        toast.success('נרשמת לרשימת ההמתנה! 📋 תקבל הודעה כשתפנה מקום בתחרות', { duration: 5000 });
      } else if (response.data.status === 'full') {
        toast.success('נרשמת בהצלחה! התחרות מלאה - ההגרלה בוצעה! 🎉', { duration: 5000 });
      } else {
        toast.success(response.data.message);
      }
      
      setPartnerName('');
      setShowPartnerInput(false);
      fetchTournamentDetails();
    } catch (error) {
      const detail = error.response?.data?.detail || 'שגיאה בהרשמה';
      if (error.response?.status === 403 && detail.includes('מושהה')) {
        toast.error(detail, { duration: 9000 });
      } else {
        toast.error(detail);
      }
    }
  };

  const handleUnregister = async () => {
    setShowCancelDialog(false);
    try {
      const response = await axios.post(`${API}/tournaments/unregister`, { tournament_id: id });

      const fee = response.data.fee_amount || 0;
      if (fee > 0) {
        toast.error(
          `ההרשמה בוטלה. חויבת בדמי ביטול בסך ${fee}₪. חשבונך יושהה עד להסדרת התשלום מול ההנהלה.`,
          { duration: 8000 }
        );
      } else if (response.data.moved_player) {
        toast.success('בוטלת בהצלחה! שחקן מרשימת ההמתנה קיבל הודעה והועבר לתחרות 📧', { duration: 5000 });
      } else {
        toast.success(response.data.message || 'ההרשמה בוטלה בהצלחה (ללא דמי ביטול)');
      }

      fetchTournamentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בביטול');
    }
  };

  const isUserRegistered = () => {
    return user && tournament && (tournament.registered_players.includes(user.id) || tournament.waiting_list?.includes(user.id));
  };

  // Admin Functions
  const handleAdminDrawGroups = async () => {
    if (!window.confirm('האם אתה בטוח שברצונך לבצע הגרלה לקבוצות? פעולה זו תיצור משחקים חדשים.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/tournaments/${id}/draw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`הגרלה בוצעה בהצלחה! נוצרו ${response.data.matches_created} משחקים`);
      fetchTournamentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בביצוע הגרלה');
    }
  };

  const handleGenerateDraw = async () => {
    console.log('🎲 התחלת הגרלה...');
    console.log('Tournament:', tournament);
    console.log('User:', currentUser);
    
    const playerCount = tournament.registered_players.length;
    const maxPlayers = tournament.max_players;
    const message = tournament.type === 'knockout' 
      ? `האם לבצע הגרלה? (${playerCount}/${maxPlayers} שחקנים)\nהפעולה תיצור ${maxPlayers === 32 ? '37' : '31'} משחקים ותמחק הגרלות קודמות.`
      : `האם לבצע הגרלה לקבוצות? (${playerCount}/${maxPlayers} שחקנים)`;
    
    if (!window.confirm(message)) {
      console.log('❌ הגרלה בוטלה על ידי המשתמש');
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('Token exists:', !!token);
      console.log('API URL:', `${API}/tournaments/${id}/generate-draw`);
      
      const response = await axios.post(
        `${API}/tournaments/${id}/generate-draw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ הגרלה הצליחה:', response.data);
      toast.success(`🎲 הגרלה הושלמה! נוצרו ${response.data.matches_count} משחקים`);
      
      // רענון אוטומטי של הדף
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('❌ שגיאה בהגרלה:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'שגיאה בביצוע הגרלה');
    }
  };

  const handleAdminResetTournament = async () => {
    console.log('🔄 התחלת איפוס טורניר...');
    
    if (!window.confirm('⚠️ האם אתה בטוח שברצונך לאפס את הטורניר? כל המשחקים ימחקו!')) {
      console.log('❌ איפוס בוטל');
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('Token exists:', !!token);
      console.log('API URL:', `${API}/admin/tournaments/${id}/reset`);
      
      const response = await axios.post(
        `${API}/admin/tournaments/${id}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ איפוס הצליח:', response.data);
      toast.success(`הטורניר אופס! נמחקו ${response.data.matches_deleted} משחקים`);
      fetchTournamentDetails();
    } catch (error) {
      console.error('❌ שגיאה באיפוס:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'שגיאה באיפוס טורניר');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Loading Skeleton */}
          <div className="animate-pulse space-y-6">
            {/* Header Skeleton */}
            <div className="bg-white/80 rounded-2xl p-6 space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="flex gap-4">
                <div className="h-10 bg-gray-200 rounded w-24"></div>
                <div className="h-10 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
            
            {/* Tabs Skeleton */}
            <div className="flex gap-2">
              <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
              <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
            </div>
            
            {/* Content Skeleton */}
            <div className="grid md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/60 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-center mt-8">
            <LoadingSpinner size="lg" text="טוען פרטי תחרות..." />
          </div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-xl text-gray-600">התחרות לא נמצאה</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Prizes Info */}
        {tournament.prizes && (
          <Card className="mb-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-600" />
                פרסי התחרות
                {isAdmin && (
                  <button
                    onClick={openPrizesModal}
                    data-testid="edit-prizes-btn"
                    className="mr-auto px-3 py-1.5 text-sm bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    ערוך פרסים
                  </button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-yellow-600 font-bold mb-2">🥇 מקום ראשון</div>
                  <div className="text-sm text-gray-700">{tournament.prizes.first_place}</div>
                </div>
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-gray-600 font-bold mb-2">🥈 מקום שני</div>
                  <div className="text-sm text-gray-700">{tournament.prizes.second_place}</div>
                </div>
                {tournament.prizes.third_place && (
                  <div className="p-4 bg-white rounded-lg shadow-sm">
                    <div className="text-orange-600 font-bold mb-2">🥉 מקום שלישי</div>
                    <div className="text-sm text-gray-700">{tournament.prizes.third_place}</div>
                  </div>
                )}
                {tournament.prizes.consolation_winner && (
                  <div className="p-4 bg-white rounded-lg shadow-sm">
                    <div className="text-blue-600 font-bold mb-2">🏅 טורניר תנחומים</div>
                    <div className="text-sm text-gray-700">{tournament.prizes.consolation_winner}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        {!tournament.prizes && isAdmin && (
          <div className="mb-8 text-center">
            <button
              onClick={openPrizesModal}
              data-testid="add-prizes-btn"
              className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 mx-auto"
            >
              <Trophy className="w-5 h-5" />
              הוסף פרסים לתחרות
            </button>
          </div>
        )}

        {/* Tournament Winner Banner - If completed */}
        {tournament.status === 'completed' && tournament.type === 'knockout' && (() => {
          const topThree = getTopThreeWinners();
          return topThree && topThree.length > 0 ? (
            <Card className="mb-6 overflow-hidden border-4 border-yellow-400 shadow-2xl">
              <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 p-8 text-center">
                <div className="flex justify-center mb-4">
                  <Trophy className="w-20 h-20 text-white animate-bounce" />
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-8 drop-shadow-lg">
                  🏆 זוכי התחרות 🏆
                </h2>
                
                {/* Top 3 Winners Podium */}
                <div className="flex items-end justify-center gap-4 mb-6 flex-wrap">
                  {/* Second Place */}
                  {topThree[1] && (
                    <div className="flex flex-col items-center">
                      <div className="bg-gradient-to-b from-gray-300 to-gray-400 rounded-t-xl p-6 w-48 border-4 border-white shadow-xl">
                        <div className="text-6xl mb-2">🥈</div>
                        <div className="text-2xl font-bold text-gray-800 mb-1">מקום 2</div>
                        <Link 
                          to={`/profile/${topThree[1].id}`}
                          className="text-xl font-semibold text-white hover:text-yellow-200 transition-colors underline block mb-2"
                        >
                          {topThree[1].name}
                        </Link>
                        {topThree[1].prize && (
                          <div className="text-sm text-gray-700 bg-white/80 rounded px-3 py-1 mt-2">
                            {topThree[1].prize}
                          </div>
                        )}
                      </div>
                      <div className="bg-gradient-to-b from-gray-400 to-gray-500 w-48 h-24 rounded-b-lg border-4 border-t-0 border-white flex items-center justify-center text-white font-bold text-3xl">
                        2
                      </div>
                    </div>
                  )}
                  
                  {/* First Place */}
                  {topThree[0] && (
                    <div className="flex flex-col items-center">
                      <div className="bg-gradient-to-b from-yellow-300 to-yellow-500 rounded-t-xl p-8 w-56 border-4 border-white shadow-2xl transform scale-110">
                        <div className="text-7xl mb-3 animate-pulse">🥇</div>
                        <div className="text-3xl font-bold text-yellow-900 mb-2">מקום 1</div>
                        <Link 
                          to={`/profile/${topThree[0].id}`}
                          className="text-2xl font-bold text-white hover:text-yellow-100 transition-colors underline block mb-2"
                        >
                          {topThree[0].name}
                        </Link>
                        {topThree[0].prize && (
                          <div className="text-sm font-semibold text-yellow-900 bg-white/90 rounded px-3 py-2 mt-3">
                            {topThree[0].prize}
                          </div>
                        )}
                      </div>
                      <div className="bg-gradient-to-b from-yellow-500 to-yellow-600 w-56 h-32 rounded-b-lg border-4 border-t-0 border-white flex items-center justify-center text-white font-bold text-4xl">
                        1
                      </div>
                    </div>
                  )}
                  
                  {/* Third Place */}
                  {topThree[2] && (
                    <div className="flex flex-col items-center">
                      <div className="bg-gradient-to-b from-orange-300 to-orange-400 rounded-t-xl p-6 w-48 border-4 border-white shadow-xl">
                        <div className="text-6xl mb-2">🥉</div>
                        <div className="text-2xl font-bold text-orange-900 mb-1">מקום 3</div>
                        <Link 
                          to={`/profile/${topThree[2].id}`}
                          className="text-xl font-semibold text-white hover:text-orange-100 transition-colors underline block mb-2"
                        >
                          {topThree[2].name}
                        </Link>
                        {topThree[2].prize && (
                          <div className="text-sm text-orange-900 bg-white/80 rounded px-3 py-1 mt-2">
                            {topThree[2].prize}
                          </div>
                        )}
                      </div>
                      <div className="bg-gradient-to-b from-orange-400 to-orange-500 w-48 h-20 rounded-b-lg border-4 border-t-0 border-white flex items-center justify-center text-white font-bold text-3xl">
                        3
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="text-white text-lg mt-4 font-semibold">
                  🎉 מזל טוב לזוכים! 🎉
                </div>
              </div>
            </Card>
          ) : null;
        })()}
        
        {/* Tournament Winner Banner for Daily Tournaments - If completed */}
        {tournament.status === 'completed' && tournament.type === 'daily' && (() => {
          const winner = getTournamentWinner();
          return winner ? (
            <Card className="mb-6 overflow-hidden border-4 border-yellow-400 shadow-2xl">
              <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 p-8 text-center">
                <div className="flex justify-center mb-4">
                  <Trophy className="w-24 h-24 text-white animate-bounce" />
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-2 drop-shadow-lg">
                  🏆 זוכה התחרות 🏆
                </h2>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 mt-6 border-2 border-white/30">
                  <Link 
                    to={`/profile/${winner.id}`}
                    className="text-5xl md:text-6xl font-black text-white hover:text-yellow-100 transition-colors drop-shadow-2xl"
                  >
                    {winner.name}
                  </Link>
                </div>
                {winner.prize && (
                  <div className="mt-6 bg-white rounded-xl p-5 shadow-xl">
                    <p className="text-2xl font-bold text-yellow-600 mb-2">🎁 הפרס</p>
                    <p className="text-xl text-gray-800 font-semibold">{winner.prize}</p>
                  </div>
                )}
              </div>
            </Card>
          ) : null;
        })()}

        {/* Tournament Header */}
        <Card className="mb-8 glass-effect" data-testid="tournament-details-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex-1">
                <CardTitle className="text-2xl sm:text-3xl md:text-4xl mb-4" data-testid="tournament-name">{tournament.name}</CardTitle>
                <div className="flex flex-wrap gap-4 text-gray-700">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span>
                      {new Date(tournament.date).toLocaleDateString('he-IL')}
                      {' '}
                      ({new Date(tournament.date).toLocaleDateString('he-IL', { weekday: 'long' })})
                    </span>
                    {tournament.start_time && (
                      <span className="text-blue-600 font-bold mr-2">
                        • תחילת משחקים: {tournament.start_time}
                      </span>
                    )}
                  </div>
                  {tournament.location && (
                    <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full">
                      <Trophy className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">{tournament.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-600" />
                    <span>{tournament.category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-600" />
                    <span>{tournament.type === 'daily' ? 'יומית' : 'נוקאאוט'}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className={
                  tournament.status === 'open' ? 'status-open' : 
                  tournament.status === 'full' ? 'status-full' : 
                  tournament.status === 'in_progress' ? 'status-in-progress' :
                  tournament.status === 'completed' ? 'status-completed' :
                  tournament.status === 'upcoming' ? 'status-upcoming' :
                  'status-in-progress'
                }>
                  {tournament.status === 'open' ? '🟢 פתוח להרשמה' : 
                   tournament.status === 'full' ? '🔴 מלא' : 
                   tournament.status === 'in_progress' ? '🟡 בעיצומה' :
                   tournament.status === 'completed' ? '✅ הסתיימה' : 
                   tournament.status === 'upcoming' ? '🔒 סגור להרשמה (יפתח בקרוב)' :
                   'בעיצומה'}
                </span>

                {/* הודעת רשימת המתנה - כשהתחרות מלאה */}
                {tournament.status === 'full' && (
                  <div 
                    data-testid="waiting-list-banner"
                    className="relative overflow-hidden rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 p-4 shadow-md"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400"></div>
                    <div className="flex items-start gap-3" dir="rtl">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center text-xl">
                        📋
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-amber-900 text-base leading-relaxed">
                          התחרות מלאה, אבל אל תוותרו!
                        </p>
                        <p className="text-amber-800 text-sm mt-1 leading-relaxed">
                          ניתן עדיין להירשם ל<span className="font-bold underline decoration-amber-400 decoration-2">רשימת ההמתנה</span> - ברגע שיתפנה מקום, תקבלו הודעה ותועברו אוטומטית לתחרות!
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* כפתור שינוי סטטוס - רק לאדמין */}
                {isAdmin && (
                  <div className="mt-2 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
                    <p className="text-xs text-purple-600 font-medium mb-2 text-center">🔐 אדמין - שנה סטטוס:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/tournaments/${tournament.id}`, { status: 'upcoming' });
                            toast.success('הסטטוס שונה ל: סגור להרשמה (יפתח בקרוב)');
                            fetchTournamentDetails();
                          } catch (err) { toast.error('שגיאה בשינוי סטטוס'); }
                        }}
                        className={`px-2 py-2 rounded-lg text-xs font-bold transition-all ${
                          tournament.status === 'upcoming' 
                            ? 'bg-blue-500 text-white ring-2 ring-blue-300' 
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        🔒 יפתח בקרוב
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/tournaments/${tournament.id}`, { status: 'open' });
                            toast.success('הסטטוס שונה ל: פתוח להרשמה');
                            fetchTournamentDetails();
                          } catch (err) { toast.error('שגיאה בשינוי סטטוס'); }
                        }}
                        className={`px-2 py-2 rounded-lg text-xs font-bold transition-all ${
                          tournament.status === 'open' 
                            ? 'bg-green-500 text-white ring-2 ring-green-300' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        🟢 פתוח
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/tournaments/${tournament.id}`, { status: 'full' });
                            toast.success('הסטטוס שונה ל: מלא');
                            fetchTournamentDetails();
                          } catch (err) { toast.error('שגיאה בשינוי סטטוס'); }
                        }}
                        className={`px-2 py-2 rounded-lg text-xs font-bold transition-all ${
                          tournament.status === 'full' 
                            ? 'bg-orange-500 text-white ring-2 ring-orange-300' 
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        🔒 מלא
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/tournaments/${tournament.id}`, { status: 'in_progress' });
                            toast.success('הסטטוס שונה ל: LIVE בעיצומה');
                            fetchTournamentDetails();
                          } catch (err) { toast.error('שגיאה בשינוי סטטוס'); }
                        }}
                        className={`px-2 py-2 rounded-lg text-xs font-bold transition-all ${
                          tournament.status === 'in_progress' 
                            ? 'bg-red-500 text-white ring-2 ring-red-300 animate-pulse' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        🔴 LIVE
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/tournaments/${tournament.id}`, { status: 'completed' });
                            toast.success('הסטטוס שונה ל: הסתיימה');
                            fetchTournamentDetails();
                          } catch (err) { toast.error('שגיאה בשינוי סטטוס'); }
                        }}
                        className={`px-2 py-2 rounded-lg text-xs font-bold transition-all ${
                          tournament.status === 'completed' 
                            ? 'bg-gray-500 text-white ring-2 ring-gray-300' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        ✅ הסתיימה
                      </button>
                    </div>
                  </div>
                )}
                {/* כפתור יצירת קישורי תשלום ב-WhatsApp - רק לאדמין */}
                {isAdmin && (
                  <div className="mt-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl" data-testid="payment-whatsapp-panel">
                    <p className="text-xs text-green-700 font-medium mb-2 text-center">📲 שלח קישורי תשלום (Paybox) ב-WhatsApp לרשומים</p>
                    <button
                      onClick={fetchPaymentWhatsapp}
                      disabled={loadingPaymentWa}
                      data-testid="payment-whatsapp-btn"
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
                    >
                      {loadingPaymentWa ? 'טוען...' : '📲 צור קישורי תשלום'}
                    </button>
                    {paymentWaLinks && paymentWaLinks.length > 0 && (() => {
                      const visibleLinks = paymentOnlyUnpaid ? paymentWaLinks.filter((t) => !t.paid) : paymentWaLinks;
                      const unpaidCount = paymentWaLinks.filter((t) => !t.paid).length;
                      return (
                      <div className="mt-3 space-y-1 max-h-64 overflow-y-auto" data-testid="payment-whatsapp-list">
                        <div className="flex items-center justify-between mb-1 sticky top-0 bg-green-50 py-1">
                          <button
                            onClick={() => setPaymentOnlyUnpaid((v) => !v)}
                            data-testid="payment-filter-unpaid-toggle"
                            className={`text-[11px] font-bold px-2 py-1 rounded-full transition-colors ${paymentOnlyUnpaid ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                          >
                            {paymentOnlyUnpaid ? `✓ רק שלא שילמו (${unpaidCount})` : `הצג את כולם (${paymentWaLinks.length})`}
                          </button>
                          <span className="text-[11px] text-gray-500">לחץ על שחקן לפתיחת WhatsApp עם קישור פייבוקס</span>
                        </div>
                        {visibleLinks.length === 0 && (
                          <p className="text-center text-xs text-gray-500 py-2" data-testid="payment-all-paid">🎉 כל השחקנים שילמו!</p>
                        )}
                        {visibleLinks.map((t) => (
                          <a
                            key={t.user_id}
                            href={t.whatsapp_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`payment-wa-link-${t.user_id}`}
                            className={`flex items-center justify-between rounded-lg px-3 py-2 transition-colors ${t.paid ? 'bg-gray-100 hover:bg-gray-200' : 'bg-green-100 hover:bg-green-200'}`}
                          >
                            <span className="text-green-700 font-bold text-sm">📲 שלח</span>
                            <span className="text-slate-800 text-sm font-semibold flex items-center gap-2">
                              {t.paid && <span className="text-[10px] bg-gray-400 text-white px-1.5 py-0.5 rounded">שילם</span>}
                              {t.name}
                            </span>
                          </a>
                        ))}
                      </div>
                      );
                    })()}
                  </div>
                )}
                {/* כפתור יצירת קישורי רשימת המתנה ב-WhatsApp - רק לאדמין וכשיש רשימת המתנה */}
                {isAdmin && tournament.waiting_list && tournament.waiting_list.length > 0 && (
                  <div className="mt-2 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl" data-testid="waitlist-whatsapp-panel">
                    <p className="text-xs text-amber-700 font-medium mb-2 text-center">📋 הודעת "התפנה מקום" ב-WhatsApp לרשימת ההמתנה</p>
                    <button
                      onClick={fetchWaitlistWhatsapp}
                      disabled={loadingWaitlistWa}
                      data-testid="waitlist-whatsapp-btn"
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
                    >
                      {loadingWaitlistWa ? 'טוען...' : '📋 צור קישורי "התפנה מקום"'}
                    </button>
                    {waitlistWaLinks && waitlistWaLinks.length > 0 && (
                      <div className="mt-3 space-y-1 max-h-64 overflow-y-auto" data-testid="waitlist-whatsapp-list">
                        <p className="text-[11px] text-gray-500 mb-1 text-center">לחץ על שחקן לפתיחת WhatsApp עם הודעת אישור הגעה (כולל תאריך ושעה):</p>
                        {waitlistWaLinks.map((t) => (
                          <a
                            key={t.user_id}
                            href={t.whatsapp_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`waitlist-wa-link-${t.user_id}`}
                            className="flex items-center justify-between rounded-lg px-3 py-2 bg-amber-100 hover:bg-amber-200 transition-colors"
                          >
                            <span className="text-amber-700 font-bold text-sm">📲 שלח</span>
                            <span className="text-slate-800 text-sm font-semibold">{t.name}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {user && user.suspended && (
                  <div data-testid="suspension-banner" className="px-4 py-3 bg-red-50 dark:bg-red-950/30 border-2 border-red-400 rounded-lg text-center space-y-1">
                    <p className="text-red-700 dark:text-red-400 font-bold">🚫 חשבונך מושהה</p>
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      קיים חוב דמי ביטול בסך {user.outstanding_fee || 0}₪. יש להסדיר את התשלום מול ההנהלה כדי להירשם לתחרויות.
                    </p>
                  </div>
                )}
                {user && tournament.registered_players.includes(user.id) && (
                  <div className="px-3 py-2 bg-green-100 border-2 border-green-400 text-green-800 font-bold rounded-lg text-center">
                    ✅ רשום לתחרות
                  </div>
                )}
                {user && tournament.waiting_list?.includes(user.id) && (
                  <div className="px-3 py-2 bg-orange-100 border-2 border-orange-400 text-orange-800 font-bold rounded-lg text-center">
                    📋 ברשימת המתנה
                  </div>
                )}
                {user && tournament.status !== 'upcoming' && (
                  tournament.status === 'in_progress' || tournament.status === 'completed' ? (
                    /* תחרות LIVE או הסתיימה - לא ניתן להירשם */
                    <div className="space-y-2">
                      {isUserRegistered() && (
                        <div className="px-3 py-2 bg-green-100 border-2 border-green-400 text-green-800 font-bold rounded-lg text-center">
                          &#10003; אתה רשום לתחרות
                        </div>
                      )}
                      {tournament.status === 'in_progress' && !isUserRegistered() && (
                        <div data-testid="live-registration-closed" className="px-4 py-4 bg-red-50 border-2 border-red-400 rounded-lg text-center">
                          <p className="text-red-700 font-bold text-lg">התחרות החלה - ההרשמה סגורה</p>
                          <p className="text-red-500 text-sm mt-1">לא ניתן להירשם לתחרות שכבר בעיצומה</p>
                        </div>
                      )}
                      {tournament.status === 'completed' && !isUserRegistered() && (
                        <div className="px-4 py-3 bg-gray-100 border-2 border-gray-300 rounded-lg text-center">
                          <p className="text-gray-600 font-bold">התחרות הסתיימה</p>
                        </div>
                      )}
                    </div>
                  ) : isUserRegistered() ? (
                    <div className="space-y-2">
                      <Button variant="destructive" onClick={() => setShowCancelDialog(true)} data-testid="unregister-btn">
                        בטל הרשמה
                      </Button>
                      {/* כפתור תשלום - תמיד מוצג */}
                      <a 
                        href={tournament.type === 'daily' 
                          ? 'https://links.payboxapp.com/5WsY9sjQLUb' 
                          : 'https://links.payboxapp.com/5tsaxw82LUb'
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold">
                          💳 לתשלום דרך Paybox
                        </Button>
                      </a>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {tournament.category?.includes('זוגות') && (
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            שם הפרטנר <span className="text-red-500">(חובה)</span>:
                          </label>
                          <input
                            type="text"
                            value={partnerName}
                            onChange={(e) => setPartnerName(e.target.value)}
                            placeholder="הכנס שם פרטנר..."
                            className="w-full border rounded px-3 py-2 text-sm"
                          />
                          <p className="text-xs text-red-500 mt-1 font-medium">
                            ⚠️ יש להזין את שם הפרטנר לפני לחיצה על "הירשם לתחרות"
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            💡 תופיעו ביחד ברשימת המשתתפים ובבתים
                          </p>
                        </div>
                      )}
                      <Button className="btn-secondary w-full" onClick={handleRegister} data-testid="register-btn">
                        הירשם לתחרות
                      </Button>
                      
                      {/* כפתור תשלום - תמיד מוצג */}
                      <a 
                        href={tournament.type === 'daily' 
                          ? 'https://links.payboxapp.com/5WsY9sjQLUb' 
                          : 'https://links.payboxapp.com/5tsaxw82LUb'
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mt-3"
                      >
                        <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold">
                          💳 לתשלום דרך Paybox
                        </Button>
                      </a>
                    </div>
                  )
                )}
                {user && tournament.status === 'upcoming' && (
                  <div className="px-4 py-3 bg-blue-50 border-2 border-blue-300 text-blue-800 font-bold rounded-lg text-center">
                    🔒 ההרשמה עדיין לא נפתחה - יפתח בקרוב!
                  </div>
                )}

                {tournament.status !== 'completed' && (
                  <div className="mt-4">
                    <CancellationPolicyCard />
                  </div>
                )}

                {tournament.status !== 'completed' && (
                  <div className="mt-3" data-testid="add-to-calendar">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                      <CalendarPlus className="w-3.5 h-3.5" /> הוספה ליומן
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        href={getGoogleCalendarUrl(tournament) || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="add-to-google-calendar"
                        className="flex items-center justify-center gap-1.5 text-xs font-semibold py-2 px-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:text-blue-600 transition-colors"
                      >
                        <CalendarPlus className="w-4 h-4" /> Google
                      </a>
                      <button
                        type="button"
                        onClick={() => downloadIcs(tournament)}
                        data-testid="add-to-apple-calendar"
                        className="flex items-center justify-center gap-1.5 text-xs font-semibold py-2 px-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:text-blue-600 transition-colors"
                      >
                        <CalendarPlus className="w-4 h-4" /> Apple / Outlook
                      </button>
                    </div>
                  </div>
                )}

                {/* Admin Controls - תמיד מוצג לרועי */}
                {user && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email) && (
                  <div className="mt-4 p-3 bg-red-50 border-2 border-red-200 rounded-lg space-y-2">
                    <div className="text-xs font-bold text-red-700 mb-2">⚙️ פקדי אדמין - רועי שפיר</div>
                    
                    {/* כפתור עריכת תחרות */}
                    <Button 
                      onClick={openEditTournamentModal}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white text-sm"
                      size="sm"
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      ✏️ ערוך פרטי תחרות
                    </Button>
                    
                    {/* כפתור הוסף שחקן - חדש ומשופר */}
                    <Button 
                      onClick={() => {
                        setIsAddPlayerModalOpen(true);
                        setPlayerSearchQuery('');
                        setPairSearchQuery1(''); setPairSearchQuery2('');
                        fetchAllUsers();
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-sm"
                      size="sm"
                    >
                      <Users className="w-4 h-4 ml-1" />
                      {isDoublesCategory ? '➕ הוסף זוג לתחרות' : '➕ הוסף שחקן לתחרות'}
                    </Button>
                    
                    {/* כפתור בצע הגרלות - תמיד מופיע */}
                    <Button 
                      onClick={handleGenerateDraw}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
                      size="sm"
                      disabled={tournament.registered_players.length === 0}
                    >
                      {tournament.registered_players.length === 0 
                        ? '🎲 בצע הגרלות (אין שחקנים רשומים)'
                        : `🎲 בצע הגרלות (${tournament.registered_players.length} שחקנים)`
                      }
                    </Button>
                    
                    {/* כפתור ערוך בתים - רק לתחרויות יומיות שיש בהן groups */}
                    {tournament.type === 'daily' && tournament.groups && Object.keys(tournament.groups).length > 0 && (
                      <Button 
                        onClick={() => { setIsEditGroupsModalOpen(true); fetchAllUsers(); }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm"
                        size="sm"
                      >
                        ✏️ ערוך בתים
                      </Button>
                    )}
                    
                    <Button 
                      onClick={handleAdminResetTournament}
                      variant="destructive"
                      className="w-full text-sm"
                      size="sm"
                    >
                      🔄 אפס טורניר
                    </Button>
                    
                    {tournament.status !== 'completed' && (
                      <Button 
                        onClick={async () => {
                          if (window.confirm('✅ לסיים את התחרות ולעדכן את כל הסטטיסטיקות לכל השחקנים? (הניקוד מתעדכן ידנית בנפרד)')) {
                            try {
                              const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                              await axios.post(
                                `${API}/admin/tournaments/${id}/finalize`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success('התחרות הסתיימה! הסטטיסטיקות עודכנו לכל המשתתפים');
                              fetchTournamentDetails();
                            } catch (error) {
                              toast.error('שגיאה בסיום תחרות');
                            }
                          }
                        }}
                        className="w-full text-sm bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        ✅ סיים תחרות
                      </Button>
                    )}

                    <Button
                      onClick={openPointsDialog}
                      className="w-full text-sm bg-amber-500 hover:bg-amber-600 text-white"
                      size="sm"
                      data-testid="distribute-points-btn"
                    >
                      <Coins className="w-4 h-4 ml-1" />
                      חשב וחלק נקודות לפי דירוג
                    </Button>
                    
                    <Button 
                      onClick={() => {
                        if (window.confirm('⚠️ למחוק את התחרות הזו לצמיתות?')) {
                          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                          axios.delete(`${API}/admin/tournaments/${id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          }).then(() => {
                            toast.success('התחרות נמחקה');
                            navigate('/tournaments/knockout');
                          }).catch(err => toast.error('שגיאה במחיקה'));
                        }
                      }}
                      variant="destructive"
                      className="w-full text-sm bg-red-700 hover:bg-red-800"
                      size="sm"
                    >
                      🗑️ מחק תחרות
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          {tournament.description && (
            <CardContent>
              <p className="text-gray-700 leading-relaxed text-right" dir="rtl">{tournament.description}</p>
            </CardContent>
          )}
        </Card>

        {/* Sponsor banner */}
        <SponsorBanner className="mb-6 mx-auto max-w-sm" />

        {/* Self check-in banner (daily, in-progress, tournament day, registered player) */}
        {checkInStatus && checkInStatus.is_registered && (checkInStatus.can_check_in || checkInStatus.already_checked_in) && (
          <div className="mb-6" data-testid="self-checkin-banner">
            {checkInStatus.already_checked_in ? (
              <div className="flex items-center justify-center gap-2 bg-emerald-50 border-2 border-emerald-300 text-emerald-800 rounded-2xl px-4 py-3 font-bold" data-testid="checkin-done">
                <UserCheck className="w-5 h-5" /> ביצעת צ'ק-אין ✓ — בהצלחה בתחרות!
              </div>
            ) : (
              <button
                onClick={handleSelfCheckIn}
                data-testid="self-checkin-btn"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-2xl px-4 py-4 font-extrabold text-lg shadow-lg transition-all"
              >
                <UserCheck className="w-6 h-6" /> אני כאן! בצע צ'ק-אין לתחרות
              </button>
            )}
          </div>
        )}

        {/* Tabs: Players, Draw */}
        {/* פודיום זוכים חגיגי */}
        <WinnersPodium tournament={tournament} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <div className="bg-gradient-to-r from-white via-gray-50 to-white rounded-2xl p-2 shadow-lg border-2 border-gray-200">
            <TabsList className="grid w-full grid-cols-2 bg-transparent gap-3">
              {/* טאב שחקנים - כחול מודרני */}
              <TabsTrigger 
                value="players"
                className={`
                  relative py-4 px-6 rounded-xl font-bold text-base transition-all duration-200 border-2
                  ${activeTab === 'players' 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-300/50 scale-[1.02] border-blue-400' 
                    : 'bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 hover:from-blue-100 hover:to-cyan-100 border-blue-200'
                  }
                `}
              >
                <Users className={`w-5 h-5 ml-2`} />
                <span className="flex items-center gap-2">
                  {isDoublesCategory 
                    ? `זוגות (${groupPlayersIntoPairs(registeredUsers).length}/${Math.floor((tournament.max_participants || 16) / 2)})`
                    : `שחקנים (${registeredUsers.length}/${tournament.max_participants || 16})`
                  }
                </span>
              </TabsTrigger>
              
              {/* טאב הגרלות/תוצאות - ירוק או זהוב */}
              <TabsTrigger 
                value="bracket"
                className={`
                  relative py-4 px-6 rounded-xl font-bold text-base transition-all duration-200 border-2
                  ${tournament.status === 'completed'
                    ? activeTab === 'bracket'
                      ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900 shadow-lg shadow-amber-300/50 scale-[1.02] border-amber-400'
                      : 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 hover:from-amber-100 hover:to-yellow-100 border-amber-200'
                    : activeTab === 'bracket'
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-green-300/50 scale-[1.02] border-emerald-400'
                      : 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 hover:from-emerald-100 hover:to-green-100 border-emerald-200'
                  }
                `}
              >
                <Trophy className={`w-5 h-5 ml-2`} />
                <span className="flex items-center gap-2">
                  {tournament.status === 'completed' ? '🏆 תוצאות התחרות' : '🎲 הגרלות'}
                </span>
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Badge for completed tournament */}
          {tournament.status === 'completed' && (
            <div className="mt-4 flex justify-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-yellow-100 border-2 border-amber-400 text-amber-800 px-6 py-2 rounded-full font-bold shadow-lg animate-pulse">
                <Trophy className="w-5 h-5 text-amber-600" />
                <span>🎉 התחרות הסתיימה!</span>
                <Trophy className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          )}

          {/* שיטת התחרות והמשחקים */}
          <Card className="my-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
            <CardContent className="pt-4">
              {/* כפתור עריכה לאדמין */}
              {user && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email) && (
                <div className="flex justify-end mb-3">
                  <Button 
                    onClick={openEditRulesModal}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Edit className="w-4 h-4 ml-1" />
                    ✏️ ערוך שיטת התחרות
                  </Button>
                </div>
              )}
              
              {/* הצגת טקסט מותאם אם קיים */}
              {tournament.rules_text ? (
                <div className="text-gray-800 space-y-4 text-right whitespace-pre-wrap" dir="rtl">
                  {tournament.rules_text}
                  
                  {/* קישור וואטסאפ תמיד יוצג */}
                  <div className="bg-green-100 p-3 rounded-lg border border-green-300 mt-4">
                    <p className="font-semibold text-green-800 mb-2">להצטרפות לקבוצה של התחרות (חובה לעדכונים):</p>
                    <a 
                      href={tournament.whatsapp_link || "https://chat.whatsapp.com/B7xnF1EjblnAbv5ixZ9kuo"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-800 underline font-medium"
                    >
                      💬 לחץ כאן להצטרפות לקבוצת הווטסאפ
                    </a>
                  </div>
                </div>
              ) : tournament.type === 'daily' ? (
                <div className="text-gray-800 space-y-4 text-right" dir="rtl">
                  <div>
                    <h3 className="font-bold text-lg text-blue-700 mb-3">📋 שיטת התחרות והמשחקים:</h3>
                    <ul className="space-y-2 mr-4">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500">•</span>
                        <span>שלב בתים (4 בתים של 4 שחקנים)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500">•</span>
                        <span>משחקים מ-2:2 עד סט מלא</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500">•</span>
                        <span>2 עולים מכל בית לשלב נוקאאוט</span>
                      </li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3 italic">(ייתכנו שינויים בהתאם לזמנים)</p>
                  </div>
                  
                  <div className="bg-green-100 p-3 rounded-lg border border-green-300">
                    <p className="font-semibold text-green-800 mb-2">להצטרפות לקבוצה של התחרות (חובה לעדכונים):</p>
                    <a 
                      href="https://chat.whatsapp.com/B7xnF1EjblnAbv5ixZ9kuo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-800 underline font-medium"
                    >
                      💬 לחץ כאן להצטרפות לקבוצת הווטסאפ
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-gray-800 space-y-4 text-right" dir="rtl">
                  <div>
                    <h3 className="font-bold text-lg text-purple-700 mb-3">✨ פרטי התחרות:</h3>
                    <div className="mr-4 space-y-2">
                      <p><span className="font-semibold">שיטת משחקים:</span></p>
                      <p className="mr-4">הטוב מ-3 מערכות, הסט השלישי הוא סופר טייבריק</p>
                      <p className="mt-2"><span className="font-semibold">תנחומים:</span> מפסידים בסיבוב הראשון בלבד יורדים לטורניר תנחומים, והמנצח בו יזכה בפרס!</p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-lg text-purple-700 mb-3">🗓️ שיטת התחרות:</h3>
                    <ul className="space-y-2 mr-4 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>התחרות תימשך כחודש וחצי, ובמהלך כל שבוע אחד חובה לשחק שלב אחד לפחות.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>השחקנים אחראים לקבוע ביניהם מיקום ושעה שמתאימים לשניהם.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>אם אין הסכמה, מנהל התחרות יקבע מיקום ושעה נייטרליים.</span>
                      </li>
                      <li className="flex items-start gap-2 bg-red-100 p-3 rounded-lg border-2 border-red-400 mt-2">
                        <span className="text-red-600 text-xl">⚠️</span>
                        <span className="text-red-700 font-bold text-lg">מי שלא יופיע – מפסיד טכני!</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>השחקנים אחראים להזמין מגרש ולשאת בעלות השכרת המגרש + הכדורים.</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-lg text-purple-700 mb-2">📊 עדכון תוצאות:</h3>
                    <p className="mr-4 text-sm">חובה על המנצח לעדכן את התוצאה למנהל התחרות.</p>
                  </div>
                  
                  <div className="bg-green-100 p-3 rounded-lg border border-green-300">
                    <p className="font-semibold text-green-800 mb-2">להצטרפות לקבוצה של התחרות (חובה לעדכונים):</p>
                    <a 
                      href="https://chat.whatsapp.com/B7xnF1EjblnAbv5ixZ9kuo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-800 underline font-medium"
                    >
                      💬 לחץ כאן להצטרפות לקבוצת הווטסאפ
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tab: Players */}
          <TabsContent value="players">
            {/* Registered Players */}
            <Card className="mb-6" dir="rtl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 justify-end">
                  <span>{isDoublesCategory 
                    ? `זוגות רשומים (${groupPlayersIntoPairs(registeredUsers).length}/${Math.floor((tournament.max_participants || 16) / 2)})`
                    : `שחקנים רשומים (${registeredUsers.length}/${tournament.max_participants || 16})`
                  }</span>
                  <UserCheck className="w-6 h-6 text-green-600" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {registeredUsers.length > 0 ? (
                  isDoublesCategory ? (
                    // תצוגה לטורנירי זוגות - הצגת זוגות בלבד
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
                      {groupPlayersIntoPairs(registeredUsers).map((pair, index) => (
                        <div key={pair.id} className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-300 flex-row-reverse">
                          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-lg text-purple-900 mb-2">
                              🎾 זוג {index + 1}
                            </div>
                            <div className="flex items-center gap-2 mb-1">
                              <Link to={`/profile/${pair.player1.id}`} className="hover:text-blue-600 font-semibold">
                                {pair.player1.full_name}
                              </Link>
                              <span className="text-purple-600 font-bold">+</span>
                              {pair.player2 ? (
                                <Link to={`/profile/${pair.player2.id}`} className="hover:text-blue-600 font-semibold">
                                  {pair.player2.full_name}
                                </Link>
                              ) : (
                                <span className="font-semibold text-gray-600">{tournament.partnerships[pair.player1.id]}</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-yellow-600">⭐ {pair.total_points} נקודות (ביחד)</span>
                              </div>
                              {tournament.registration_timestamps?.[pair.player1.id] && (
                                <div className="text-xs text-slate-500 flex items-center gap-1">
                                  <span>🕐</span>
                                  <span>נרשמו: {new Date(tournament.registration_timestamps[pair.player1.id]).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* סימון תשלום - רק לאדמין - זוגות */}
                          {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && (
                            <div 
                              className="flex flex-col items-center gap-1 mr-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                    // עדכן סטטוס תשלום לשני השחקנים בזוג
                                    await axios.post(
                                      `${API}/admin/tournaments/${id}/toggle-payment/${pair.player1.id}`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    if (pair.player2) {
                                      await axios.post(
                                        `${API}/admin/tournaments/${id}/toggle-payment/${pair.player2.id}`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                    }
                                    toast.success('סטטוס תשלום עודכן לזוג');
                                    fetchTournamentDetails();
                                  } catch (error) {
                                    toast.error('שגיאה בעדכון סטטוס תשלום');
                                  }
                                }}
                                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                                  (tournament.paid_players?.includes(pair.player1.id) && 
                                   (pair.player2 ? tournament.paid_players?.includes(pair.player2.id) : true))
                                    ? 'bg-green-500 border-green-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-400 hover:border-green-500'
                                }`}
                                title={(tournament.paid_players?.includes(pair.player1.id) && 
                                       (pair.player2 ? tournament.paid_players?.includes(pair.player2.id) : true))
                                  ? 'שילם ✓' : 'לא שילם'}
                              >
                                {(tournament.paid_players?.includes(pair.player1.id) && 
                                  (pair.player2 ? tournament.paid_players?.includes(pair.player2.id) : true))
                                  ? '✓' : '💳'}
                              </button>
                              <span className="text-xs font-semibold">
                                {(tournament.paid_players?.includes(pair.player1.id) && 
                                  (pair.player2 ? tournament.paid_players?.includes(pair.player2.id) : true))
                                  ? <span className="text-green-600">שילם</span> 
                                  : <span className="text-red-600">לא שילם</span>
                                }
                              </span>
                            </div>
                          )}
                          
                          {/* סימון נוכחות - רק לאדמין - זוגות - רק תחרויות יומיות */}
                          {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && tournament.type === 'daily' && (
                            <div 
                              className="flex flex-col items-center gap-1 mr-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                    // עדכן סטטוס נוכחות לשני השחקנים בזוג
                                    await axios.post(
                                      `${API}/admin/tournaments/${id}/toggle-attendance/${pair.player1.id}`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    if (pair.player2) {
                                      await axios.post(
                                        `${API}/admin/tournaments/${id}/toggle-attendance/${pair.player2.id}`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                    }
                                    toast.success('סטטוס נוכחות עודכן לזוג');
                                    fetchTournamentDetails();
                                  } catch (error) {
                                    toast.error('שגיאה בעדכון סטטוס נוכחות');
                                  }
                                }}
                                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                                  (tournament.attended_players?.includes(pair.player1.id) && 
                                   (pair.player2 ? tournament.attended_players?.includes(pair.player2.id) : true))
                                    ? 'bg-blue-500 border-blue-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-400 hover:border-blue-500'
                                }`}
                                title={(tournament.attended_players?.includes(pair.player1.id) && 
                                       (pair.player2 ? tournament.attended_players?.includes(pair.player2.id) : true))
                                  ? 'הגיע ✓' : 'לא הגיע'}
                              >
                                {(tournament.attended_players?.includes(pair.player1.id) && 
                                  (pair.player2 ? tournament.attended_players?.includes(pair.player2.id) : true))
                                  ? '✓' : '👤'}
                              </button>
                              <span className="text-xs font-semibold">
                                {(tournament.attended_players?.includes(pair.player1.id) && 
                                  (pair.player2 ? tournament.attended_players?.includes(pair.player2.id) : true))
                                  ? <span className="text-blue-600">הגיע</span> 
                                  : <span className="text-orange-600">לא הגיע</span>
                                }
                              </span>
                            </div>
                          )}
                          
                          {(pair.player1.id === currentUser?.id || pair.player2?.id === currentUser?.id) && (
                            <Badge className="bg-green-600">זוג שלך!</Badge>
                          )}
                          {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && (
                            <Button
                              onClick={async () => {
                                const confirmMsg = pair.player2 
                                  ? `להסיר את הזוג ${pair.player1.full_name} + ${pair.player2.full_name} מהתחרות?`
                                  : `להסיר את ${pair.pairName} מהתחרות?`;
                                if (window.confirm(confirmMsg)) {
                                  try {
                                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                    // הסר את שני השחקנים
                                    await axios.post(
                                      `${API}/admin/tournaments/${id}/remove-player/${pair.player1.id}`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    if (pair.player2) {
                                      await axios.post(
                                        `${API}/admin/tournaments/${id}/remove-player/${pair.player2.id}`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                    }
                                    toast.success('הזוג הוסר מהתחרות');
                                    fetchTournamentDetails();
                                  } catch (error) {
                                    toast.error('שגיאה בהסרת זוג');
                                  }
                                }
                              }}
                              variant="destructive"
                              size="sm"
                            >
                              🗑️
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // תצוגה רגילה לטורנירי יחידים
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" dir="rtl">
                      {registeredUsers.map((user, index) => (
                        <div key={user.id} className={`flex items-center gap-3 p-4 rounded-lg border-2 ${
                          user.is_external 
                            ? 'bg-gradient-to-l from-orange-50 to-yellow-50 border-orange-200' 
                            : 'bg-gradient-to-l from-slate-50 to-indigo-50 border-indigo-200'
                        }`}>
                          {/* צד ימין - מספר ותמונה */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${
                            user.is_external ? 'bg-orange-500' : 'bg-indigo-600'
                          }`}>
                            {index + 1}
                          </div>
                          <Avatar className="w-10 h-10 flex-shrink-0">
                            {user.avatar && <AvatarImage src={user.avatar} alt={user.full_name} />}
                            <AvatarFallback className={user.is_external ? 'bg-orange-600 text-white' : 'bg-slate-600 text-white'}>
                              {user.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          
                          {/* מרכז - שם ופרטים */}
                          <div className="flex-1 text-right">
                            {user.is_external ? (
                              <div className="font-bold text-lg">
                                {user.full_name}
                                <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full mr-2">שחקן חיצוני</span>
                              </div>
                            ) : (
                              <Link to={`/profile/${user.id}`} className="hover:text-indigo-600 font-bold text-lg">
                                {user.full_name}
                              </Link>
                            )}
                            <div className="text-sm text-gray-600 space-y-1">
                              {!user.is_external && (
                                <div className="flex items-center gap-2 justify-end">
                                  <span className="font-semibold text-yellow-600">⭐ {user.total_points || 0} נק׳</span>
                                  {user.category_rank && (
                                    <span className="font-semibold text-indigo-600">🏆 #{user.category_rank}</span>
                                  )}
                                </div>
                              )}
                              {tournament.registration_timestamps?.[user.id] && (
                                <div className="text-xs text-slate-500 flex items-center gap-1 justify-end" data-testid={`reg-time-${user.id}`}>
                                  <span>🕐</span>
                                  <span>נרשם: {new Date(tournament.registration_timestamps[user.id]).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                              )}
                              {tournament.type === 'knockout' && (
                                <div className="text-xs text-gray-500 text-right">
                                  {user.phone && (isAdmin || (currentUser && tournament.registered_players.includes(currentUser.id))) && (() => {
                                    const cleanPhone = user.phone.replace(/[-\s()]/g, '');
                                    const intlPhone = cleanPhone.startsWith('0') ? '972' + cleanPhone.slice(1) : cleanPhone.startsWith('+') ? cleanPhone.slice(1) : cleanPhone;
                                    const firstName = (user.full_name || '').split(' ')[0] || '';
                                    const text = encodeURIComponent(`היי ${firstName}, מדבר/ת מ-Tennis Pro Israel 🎾`);
                                    return (
                                      <a href={`https://wa.me/${intlPhone}?text=${text}`} target="_blank" rel="noopener noreferrer"
                                        data-testid={`player-wa-${user.id}`}
                                        className="inline-flex items-center gap-1 text-green-700 hover:text-green-800 hover:underline">
                                        <span>📱</span><span dir="ltr">{user.phone}</span>
                                      </a>
                                    );
                                  })()}
                                  {user.city && <div>📍 {user.city}</div>}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* צד שמאל - כפתורי סטטוס */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* סימון נוכחות - רק לאדמין ורק תחרויות יומיות */}
                            {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && tournament.type === 'daily' && (
                              <div 
                                className="flex flex-col items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                      const response = await axios.post(
                                        `${API}/admin/tournaments/${id}/toggle-attendance/${user.id}`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      const updatedTournament = { ...tournament };
                                      if (!updatedTournament.attended_players) {
                                        updatedTournament.attended_players = [];
                                      }
                                      if (response.data.attended) {
                                        if (!updatedTournament.attended_players.includes(user.id)) {
                                          updatedTournament.attended_players.push(user.id);
                                        }
                                      } else {
                                        updatedTournament.attended_players = updatedTournament.attended_players.filter(id => id !== user.id);
                                      }
                                      setTournament(updatedTournament);
                                      toast.success(response.data.message);
                                    } catch (error) {
                                      toast.error('שגיאה בעדכון סטטוס נוכחות');
                                    }
                                  }}
                                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                                    tournament.attended_players?.includes(user.id)
                                      ? 'bg-blue-500 border-blue-600 text-white'
                                      : 'bg-white border-gray-300 text-gray-400 hover:border-blue-500'
                                  }`}
                                  title={tournament.attended_players?.includes(user.id) ? 'הגיע ✓' : 'לא הגיע'}
                                >
                                  {tournament.attended_players?.includes(user.id) ? '✓' : '👤'}
                                </button>
                                <span className="text-xs font-semibold">
                                  {tournament.attended_players?.includes(user.id) ? 
                                    <span className="text-blue-600">הגיע</span> : 
                                    <span className="text-orange-600">לא הגיע</span>
                                  }
                                </span>
                              </div>
                            )}
                          
                            {/* סימון תשלום - רק לאדמין */}
                            {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && (
                              <div 
                                className="flex flex-col items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                      const response = await axios.post(
                                        `${API}/admin/tournaments/${id}/toggle-payment/${user.id}`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      const updatedTournament = { ...tournament };
                                      if (!updatedTournament.paid_players) {
                                        updatedTournament.paid_players = [];
                                      }
                                      if (response.data.paid) {
                                        if (!updatedTournament.paid_players.includes(user.id)) {
                                          updatedTournament.paid_players.push(user.id);
                                        }
                                      } else {
                                        updatedTournament.paid_players = updatedTournament.paid_players.filter(id => id !== user.id);
                                      }
                                      setTournament(updatedTournament);
                                      toast.success(response.data.message);
                                    } catch (error) {
                                      toast.error('שגיאה בעדכון סטטוס תשלום');
                                    }
                                  }}
                                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                                    tournament.paid_players?.includes(user.id)
                                      ? 'bg-green-500 border-green-600 text-white'
                                      : 'bg-white border-gray-300 text-gray-400 hover:border-green-500'
                                  }`}
                                  title={tournament.paid_players?.includes(user.id) ? 'שילם ✓' : 'לא שילם'}
                                  data-testid={`toggle-payment-${user.id}`}
                                >
                                  {tournament.paid_players?.includes(user.id) ? '✓' : '💳'}
                                </button>
                                <span className="text-xs font-semibold">
                                  {tournament.paid_players?.includes(user.id) ? 
                                    <span className="text-green-600">שילם</span> : 
                                    <span className="text-red-600">לא שילם</span>
                                  }
                                </span>
                              </div>
                            )}
                            
                            {/* כפתור הסרה - רק לאדמין */}
                            {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`להסיר את ${user.full_name} מהתחרות?`)) {
                                    try {
                                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                      await axios.post(
                                        `${API}/admin/tournaments/${id}/remove-player/${user.id}`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('שחקן הוסר מהתחרות');
                                      fetchTournamentDetails();
                                    } catch (error) {
                                      toast.error('שגיאה בהסרת שחקן');
                                    }
                                  }
                                }}
                                className="w-6 h-6 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center text-sm"
                                title="הסר שחקן"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          
                          {user.id === currentUser?.id && (
                            <Badge className="bg-green-600 flex-shrink-0">זה אתה!</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    עדיין אין שחקנים רשומים
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Waiting List */}
            {tournament.waiting_list && tournament.waiting_list.length > 0 && (
              <Card dir="rtl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 justify-end">
                    <span>רשימת המתנה ({tournament.waiting_list.length})</span>
                    <Loader className="w-6 h-6 text-orange-600" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-orange-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-orange-800 text-right">
                      💡 <strong>מה זה רשימת המתנה?</strong> כאשר מישהו מבטל הרשמה, כל הממתינים מקבלים הודעה. רק האדמין יכול להעלות שחקן מהרשימה ידנית (לחיצה על "↑ העבר לרשומים").
                    </p>
                  </div>
                  <div className="space-y-2">
                    {waitingUsers.map((user, index) => (
                      <div key={user.id} className="flex items-center gap-3 p-4 bg-gradient-to-l from-orange-50 to-yellow-50 rounded-lg border-2 border-orange-200">
                        {/* צד ימין - מספר ותמונה */}
                        <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {index + 1}
                        </div>
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          {user.avatar && <AvatarImage src={user.avatar} alt={user.full_name} />}
                          <AvatarFallback className="bg-orange-600 text-white">
                            {user.full_name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        
                        {/* מרכז - שם ופרטים */}
                        <div className="flex-1 text-right">
                          <Link to={`/profile/${user.id}`} className="hover:text-orange-600 font-bold text-lg">
                            {user.full_name}
                          </Link>
                          {user.skill_level && (
                            <div className="text-sm text-gray-600">
                              {user.skill_level}
                            </div>
                          )}
                        </div>
                        
                        {/* צד שמאל - תגיות וכפתורים */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {index === 0 && (
                            <Badge className="bg-orange-600">הבא בתור!</Badge>
                          )}
                          {user.id === currentUser?.id && (
                            <Badge className="bg-orange-600">זה אתה!</Badge>
                          )}
                          {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && (
                            <Button
                              onClick={async () => {
                                if (window.confirm(`להעביר את ${user.full_name} לרשימת הרשומים? תישלח לו הודעה אישית.`)) {
                                  try {
                                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                    await axios.post(
                                      `${API}/admin/tournaments/${id}/promote-from-waiting/${user.id}`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success(`${user.full_name} הועבר לרשומים והודעה נשלחה`);
                                    fetchTournamentDetails();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'שגיאה בהעברה');
                                  }
                                }
                              }}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-xs"
                              data-testid={`promote-waiting-${user.id}`}
                            >
                              ↑ העבר לרשומים
                            </Button>
                          )}
                          {currentUser && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email) && (
                            <Button
                              onClick={async () => {
                                if (window.confirm(`האם למחוק את ${user.full_name} מרשימת ההמתנה?`)) {
                                  try {
                                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                    await axios.post(
                                      `${API}/admin/tournaments/${id}/remove-player/${user.id}`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success(`${user.full_name} הוסר מרשימת ההמתנה`);
                                    fetchTournamentDetails();
                                  } catch (error) {
                                    toast.error('שגיאה במחיקה');
                                  }
                                }
                              }}
                              size="sm"
                              variant="destructive"
                              className="text-xs"
                            >
                              🗑️ מחק
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Bracket */}
          <TabsContent value="bracket">
            {tournament.type === 'daily' && tournament.groups && (
              <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>
                    {isDoublesCategory ? '🏠 חלוקה לבתים - זוגות' : '🏠 חלוקה לבתים (4 ראשי בתים)'}
                  </CardTitle>
                  <div className="flex gap-2">
                    {/* כפתור תקן בתים - אם יש בעיה בנתוני הבתים */}
                    {currentUser && (currentUser.is_admin || ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email)) && tournament.groups && 
                      Object.values(tournament.groups).some(g => Array.isArray(g) && g.length === 0) && (
                      <Button
                        data-testid="heal-groups-btn"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                            const r = await axios.post(
                              `${API}/admin/tournaments/${id}/heal-groups`,
                              {},
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success(`תוקן: ${r.data.players_converted} שחקנים`);
                            fetchTournamentDetails();
                          } catch (err) {
                            toast.error('שגיאה בתיקון בתים');
                          }
                        }}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold"
                      >
                        🔧 תקן בתים
                      </Button>
                    )}
                    {/* כפתור ערוך בתים - בולט */}
                    {currentUser && (currentUser.is_admin || ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(currentUser.email)) && (
                      <Button 
                        onClick={() => { setIsEditGroupsModalOpen(true); fetchAllUsers(); }}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                      >
                        ✏️ ערוך בתים
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.keys(tournament.groups).sort().map(groupLetter => {
                      const groupPlayers = tournament.groups[groupLetter] || [];
                      
                      return (
                        <div key={groupLetter} className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50" data-testid={`group-card-${groupLetter}`}>
                          <h3 className="text-xl font-bold text-center mb-3 text-blue-800">
                            בית {groupLetter}
                          </h3>
                          <div className="space-y-2">
                            {groupPlayers.length === 0 && (
                              <div className="text-center text-gray-400 italic py-4">
                                לא הוקצו שחקנים לבית זה
                              </div>
                            )}
                            {groupPlayers.map((player, idx) => {
                              // תמיכה בשני פורמטים: string (ID ישן) או object {id, name, points}
                              const playerId = typeof player === 'string' ? player : player?.id;
                              let playerName;
                              let playerPoints = 0;
                              if (typeof player === 'object' && player !== null) {
                                playerName = player.name;
                                playerPoints = player.points || 0;
                              } else {
                                // string ID - חפש את השם
                                const fromReg = registeredUsers.find(u => u.id === playerId);
                                const fromAll = allUsers.find(u => u.id === playerId);
                                const ext = tournament.external_players?.[playerId];
                                playerName = fromReg?.full_name || fromAll?.full_name || ext?.full_name || playerId;
                                playerPoints = fromReg?.total_points || fromAll?.total_points || 0;
                              }
                              
                              return (
                                <div 
                                  key={playerId || idx} 
                                  className={`p-3 rounded ${idx === 0 ? 'bg-yellow-100 border-2 border-yellow-500' : 'bg-white'}`}
                                  data-testid={`group-${groupLetter}-player-${idx}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold">{playerName || 'שחקן לא ידוע'}</span>
                                        {idx === 0 && <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded">ראש בית</span>}
                                      </div>
                                    </div>
                                    <span className="text-sm text-gray-600">{playerPoints} נקודות</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* 🎬 שחזור הטורניר */}
            {tournament.status === 'completed' && matches.some((m) => m.completed && m.winner_id) && (
              <button
                onClick={() => setShowReplay(true)}
                data-testid="open-replay-btn"
                className="w-full mb-6 py-4 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-400/60 text-amber-300 font-extrabold text-lg hover:border-amber-400 hover:shadow-lg hover:shadow-amber-400/20 transition-all flex items-center justify-center gap-3"
              >
                🎬 צפה בשחזור הטורניר
                <span className="text-xs font-normal text-slate-400">כל התוצאות שלב אחרי שלב</span>
              </button>
            )}
            <TournamentReplay
              open={showReplay}
              onClose={() => setShowReplay(false)}
              matches={matches}
              players={registeredUsers}
              tournamentName={tournament.name}
              tournamentDate={tournament.date}
            />

            <LiveResultsFeed matches={matches} players={registeredUsers} isLive={tournament.status === 'in_progress'} />

            <TournamentBracketInteractive 
              tournamentId={id} 
              tournamentType={tournament.type}
              category={tournament.category}
              tournamentName={tournament.name}
              tournamentDate={tournament.date}
            />
          </TabsContent>
        </Tabs>

        {/* ========== גלריית תמונות ========== */}
        <Card className="mt-8" dir="rtl">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera className="w-6 h-6 text-purple-600" />
                <span>גלריית תמונות</span>
                {gallery && gallery.length > 0 && (
                  <Badge className="bg-purple-100 text-purple-700">{gallery.length} תמונות</Badge>
                )}
              </div>
              
              {/* כפתור העלאת תמונה - לכל המשתמשים המחוברים */}
              {user && (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleGalleryUpload}
                    ref={galleryInputRef}
                    className="hidden"
                  />
                  <Button
                    onClick={() => galleryInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader className="w-4 h-4 ml-2 animate-spin" />
                        מעלה...
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 ml-2" />
                        העלה תמונה
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {gallery && gallery.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {gallery.map((image, index) => {
                  // תמיכה גם באובייקט עם url וגם ב-string ישיר
                  const imageUrl = typeof image === 'string' ? image : image.url;
                  return (
                    <div 
                      key={index} 
                      className="relative group aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-400 transition-all cursor-pointer shadow-sm hover:shadow-lg"
                      onClick={() => setSelectedImage({ url: imageUrl, index })}
                    >
                      <img 
                        src={imageUrl} 
                        alt={`תמונה ${index + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      
                      {/* כפתור מחיקה - רק לאדמין */}
                      {user && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGalleryImage(index);
                          }}
                          className="absolute top-2 left-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                          title="מחק תמונה"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Image className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg mb-2">אין תמונות בגלריה עדיין</p>
                <p className="text-gray-400 text-sm">
                  {user ? 'לחץ על "העלה תמונה" כדי להוסיף תמונות מהתחרות' : 'התחבר כדי להעלות תמונות'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal תצוגת תמונה מוגדלת */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
            >
              <X className="w-10 h-10" />
            </button>
            
            {/* ניווט בין תמונות */}
            {gallery && gallery.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = selectedImage.index > 0 ? selectedImage.index - 1 : gallery.length - 1;
                    setSelectedImage({ url: gallery[newIndex], index: newIndex });
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-3"
                >
                  <ArrowRight className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = selectedImage.index < gallery.length - 1 ? selectedImage.index + 1 : 0;
                    setSelectedImage({ url: gallery[newIndex], index: newIndex });
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-3 rotate-180"
                >
                  <ArrowRight className="w-8 h-8" />
                </button>
              </>
            )}
            
            <img 
              src={selectedImage.url} 
              alt="תמונה מוגדלת"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* מחיקה מהמודל - רק לאדמין */}
            {user && ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteGalleryImage(selectedImage.index);
                }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg flex items-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                מחק תמונה
              </button>
            )}
            
            <div className="absolute bottom-4 right-4 text-white bg-black/50 px-4 py-2 rounded-lg">
              {selectedImage.index + 1} / {gallery?.length}
            </div>
          </div>
        )}
      </div>
      
      {/* Modal עריכת בתים */}
      {isEditGroupsModalOpen && tournament?.groups && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 z-10">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-2xl font-bold">✏️ עריכת בתים</h2>
                <button 
                  onClick={() => { setIsEditGroupsModalOpen(false); setSwapMode(false); setSwapSelection(null); }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                  data-testid="close-edit-groups-modal"
                >
                  ×
                </button>
              </div>
              
              {/* Mode switcher + help text */}
              <div className="flex flex-wrap gap-2 items-center">
                <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="group-edit-mode-switcher">
                  <button
                    onClick={() => { setSwapMode(false); setSwapSelection(null); }}
                    data-testid="mode-move-btn"
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      !swapMode ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ➡️ העברה
                  </button>
                  <button
                    onClick={() => { setSwapMode(true); setSwapSelection(null); }}
                    data-testid="mode-swap-btn"
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      swapMode ? 'bg-purple-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    🔄 החלפה
                  </button>
                </div>
                
                {!swapMode && (
                  <span className="text-sm text-gray-600">
                    לחץ על אות הבית ליד שחקן כדי להעבירו
                  </span>
                )}
                {swapMode && (
                  <span className="text-sm text-purple-700 font-medium">
                    {swapSelection
                      ? `נבחר: ${swapSelection.playerName} (${swapSelection.groupName}). בחר שחקן מבית אחר להחלפה.`
                      : 'בחר את השחקן הראשון להחלפה'}
                  </span>
                )}
                
                {swapMode && swapSelection && (
                  <button
                    onClick={() => setSwapSelection(null)}
                    className="text-sm text-red-600 hover:underline"
                  >
                    ביטול בחירה
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {Object.entries(tournament.groups).map(([groupName, players]) => (
                <div key={groupName} className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="text-xl font-bold mb-3 text-blue-800">{groupName}</h3>
                  <div className="space-y-2">
                    {players.map((player, idx) => {
                      // Support both old format (string ID) and new format ({id, name, points})
                      const playerId = typeof player === 'string' ? player : player.id;
                      const playerName = typeof player === 'string' 
                        ? (registeredUsers.find(u => u.id === player)?.full_name 
                           || allUsers.find(u => u.id === player)?.full_name 
                           || tournament.external_players?.[player]?.full_name 
                           || player)
                        : player.name;
                      const playerPoints = typeof player === 'string'
                        ? (registeredUsers.find(u => u.id === player)?.total_points || 0)
                        : (player.points || 0);
                      
                      return (
                      <div 
                        key={playerId} 
                        className={`flex items-center justify-between p-3 rounded border transition-all ${
                          swapMode && swapSelection?.playerId === playerId
                            ? 'bg-purple-100 border-purple-500 border-2 ring-2 ring-purple-300'
                            : 'bg-white'
                        }`}
                        data-testid={`group-player-row-${playerId}`}
                      >
                        <div 
                          className={swapMode ? 'cursor-pointer flex-1' : 'flex-1'}
                          onClick={() => swapMode && handleSwapSelect(playerId, playerName, groupName)}
                        >
                          <span className="font-semibold">{idx + 1}. {playerName}</span>
                          {tournament.partnerships && tournament.partnerships[playerId] && (
                            <span className="text-sm text-purple-600 font-semibold mr-2">
                              + {tournament.partnerships[playerId]}
                            </span>
                          )}
                          <span className="text-sm text-gray-600 ml-2">({playerPoints} נקודות)</span>
                        </div>
                        <div className="flex gap-1 items-center flex-wrap justify-end">
                          {/* מצב העברה: כפתורי בתים מהירים */}
                          {!swapMode && Object.keys(tournament.groups).filter(g => g !== groupName).map(g => (
                            <button
                              key={g}
                              data-testid={`move-${playerId}-to-${g}`}
                              disabled={movingPlayer}
                              onClick={() => handleQuickMovePlayer(playerId, playerName, groupName, g)}
                              title={`העבר לבית ${g}`}
                              className="bg-blue-100 hover:bg-blue-600 hover:text-white text-blue-800 font-bold w-9 h-9 rounded-md text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {g}
                            </button>
                          ))}
                          
                          {/* מצב החלפה: כפתור בחירה */}
                          {swapMode && (
                            <button
                              data-testid={`swap-select-${playerId}`}
                              disabled={movingPlayer}
                              onClick={() => handleSwapSelect(playerId, playerName, groupName)}
                              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all disabled:opacity-50 ${
                                swapSelection?.playerId === playerId
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-purple-100 hover:bg-purple-600 hover:text-white text-purple-800'
                              }`}
                            >
                              {swapSelection?.playerId === playerId ? '✓ נבחר' : 'בחר'}
                            </button>
                          )}
                          
                          {/* כפתור הסרה */}
                          {!swapMode && (
                            <button
                              data-testid={`remove-player-${playerId}`}
                              onClick={async () => {
                                if (window.confirm(`להסיר את ${playerName} מהבית?`)) {
                                  try {
                                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                    await axios.post(
                                      `${API}/admin/tournaments/${id}/remove-player-from-group`,
                                      { user_id: playerId, group_name: groupName },
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success(`${playerName} הוסר מהבית`);
                                    fetchTournamentDetails();
                                  } catch (error) {
                                    toast.error('שגיאה בהסרת שחקן');
                                  }
                                }
                              }}
                              className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs mr-1"
                              title="הסר מהבית"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                    
                    {/* הוסף שחקן/זוג לבית */}
                    <div className="mt-3 pt-3 border-t">
                      {isDoublesCategory ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-600">הוסף זוג לבית:</p>
                          <input
                            type="text"
                            id={`pair-name1-${groupName}`}
                            placeholder="שם שחקן 1 *"
                            className="w-full text-sm border rounded px-2 py-2"
                          />
                          <input
                            type="text"
                            id={`pair-name2-${groupName}`}
                            placeholder="שם שחקן 2 (פרטנר) *"
                            className="w-full text-sm border rounded px-2 py-2"
                          />
                          <button
                            onClick={async () => {
                              const name1 = document.getElementById(`pair-name1-${groupName}`).value.trim();
                              const name2 = document.getElementById(`pair-name2-${groupName}`).value.trim();
                              if (!name1 || !name2) {
                                toast.error('יש להזין שמות לשני השחקנים');
                                return;
                              }
                              try {
                                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                await axios.post(
                                  `${API}/admin/tournaments/${id}/add-pair-to-group`,
                                  {
                                    player1_name: name1,
                                    player2_name: name2,
                                    group_name: groupName
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                toast.success(`${name1} + ${name2} נוספו לבית ${groupName}`);
                                document.getElementById(`pair-name1-${groupName}`).value = '';
                                document.getElementById(`pair-name2-${groupName}`).value = '';
                                fetchTournamentDetails();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'שגיאה בהוספת זוג');
                              }
                            }}
                            className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm font-semibold"
                          >
                            ➕ הוסף זוג לבית
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {/* הוספה מרשימת שחקנים רשומים */}
                          <div className="flex gap-2">
                            <select
                              id={`add-to-${groupName}`}
                              className="flex-1 text-sm border rounded px-2 py-2"
                              defaultValue=""
                            >
                              <option value="">בחר שחקן להוספה...</option>
                              {(() => {
                                const allPlayersInGroups = Object.values(tournament.groups).flat().map(p => p.id || p);
                                const waitingIds = tournament.waiting_list || [];
                                // 1. שחקנים ברשימת המתנה (עדיפות גבוהה)
                                const waitingUsers = allUsers.filter(u =>
                                  waitingIds.includes(u.id) && !allPlayersInGroups.includes(u.id)
                                );
                                // 2. שחקנים רשומים שלא בבית
                                const regUsers = registeredUsers.filter(u =>
                                  !allPlayersInGroups.includes(u.id) && !waitingIds.includes(u.id)
                                );
                                // 3. שאר המשתמשים באתר
                                const registeredIds = new Set(registeredUsers.map(r => r.id));
                                const otherUsers = allUsers.filter(u =>
                                  !allPlayersInGroups.includes(u.id) && !waitingIds.includes(u.id) && !registeredIds.has(u.id)
                                );
                                return (
                                  <>
                                    {waitingUsers.length > 0 && (
                                      <optgroup label={`⏳ רשימת המתנה (${waitingUsers.length}) — מומלץ!`}>
                                        {waitingUsers.map(u => (
                                          <option key={u.id} value={u.id}>⏳ {u.full_name} ({u.total_points || 0} נק׳)</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {regUsers.length > 0 && (
                                      <optgroup label="✅ רשומים לתחרות">
                                        {regUsers.map(u => (
                                          <option key={u.id} value={u.id}>{u.full_name} ({u.total_points || 0} נק׳)</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {otherUsers.length > 0 && (
                                      <optgroup label="👥 שאר המשתמשים באתר">
                                        {otherUsers.map(u => (
                                          <option key={u.id} value={u.id}>⭐ {u.full_name} ({u.total_points || 0} נק׳)</option>
                                        ))}
                                      </optgroup>
                                    )}
                                  </>
                                );
                              })()}
                            </select>
                            <button
                              onClick={async () => {
                                const select = document.getElementById(`add-to-${groupName}`);
                                const userId = select.value;
                                if (!userId) {
                                  toast.error('בחר שחקן');
                                  return;
                                }
                                try {
                                  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                  await axios.post(
                                    `${API}/admin/tournaments/${id}/add-player-to-group`,
                                    {
                                      user_id: userId,
                                      group_name: groupName
                                    },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success('שחקן נוסף לבית!');
                                  select.value = '';
                                  fetchTournamentDetails();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'שגיאה בהוספת שחקן');
                                }
                              }}
                              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm font-semibold"
                            >
                              ➕ הוסף
                            </button>
                          </div>
                          {/* הוספת שחקן חיצוני */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              id={`add-external-${groupName}`}
                              placeholder="שם שחקן חיצוני..."
                              className="flex-1 text-sm border rounded px-2 py-2"
                            />
                            <button
                              onClick={async () => {
                                const input = document.getElementById(`add-external-${groupName}`);
                                const playerName = input.value.trim();
                                if (!playerName) {
                                  toast.error('הזן שם שחקן');
                                  return;
                                }
                                try {
                                  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                  // First add external player to tournament
                                  const addRes = await axios.post(
                                    `${API}/admin/tournaments/${id}/add-external-player`,
                                    { full_name: playerName },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  const externalId = addRes.data.player_id;
                                  // Then add to group
                                  await axios.post(
                                    `${API}/admin/tournaments/${id}/add-player-to-group`,
                                    {
                                      user_id: externalId,
                                      group_name: groupName
                                    },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(`${playerName} נוסף לבית ${groupName}!`);
                                  input.value = '';
                                  fetchTournamentDetails();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'שגיאה בהוספת שחקן חיצוני');
                                }
                              }}
                              className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded text-sm font-semibold"
                            >
                              ➕ חיצוני
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setIsEditGroupsModalOpen(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  סיום
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal הוספת שחקן - החדש */}

      {/* Modal הוספת שחקן/זוג חדש */}
      {isAddPlayerModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" dir="rtl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{isDoublesCategory ? '➕ הוסף זוג לתחרות' : '➕ הוסף שחקן לתחרות'}</h2>
              <button
                onClick={() => {
                  setIsAddPlayerModalOpen(false);
                  setSelectedPlayerToAdd('');
                  setExternalPlayerName('');
                  setExternalPlayerPhone('');
                  setPlayerSearchQuery('');
                  setPairPlayer1Id(''); setPairPlayer1Name(''); setPairPlayer1Phone('');
                  setPairPlayer2Id(''); setPairPlayer2Name(''); setPairPlayer2Phone('');
                  setPairSearchQuery1(''); setPairSearchQuery2('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {isDoublesCategory ? (
              /* ========== מודל הוספת זוג ========== */
              <div className="space-y-5">
                {/* שחקן 1 */}
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/50">
                  <h3 className="font-bold text-blue-700 mb-3">שחקן 1</h3>
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => { setPairPlayer1Type('registered'); setPairPlayer1Name(''); setPairPlayer1Phone(''); }}
                      className={`flex-1 py-1.5 text-sm rounded-lg font-medium ${pairPlayer1Type === 'registered' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                    >
                      שחקן רשום
                    </button>
                    <button
                      onClick={() => { setPairPlayer1Type('external'); setPairPlayer1Id(''); }}
                      className={`flex-1 py-1.5 text-sm rounded-lg font-medium ${pairPlayer1Type === 'external' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                    >
                      שחקן חיצוני
                    </button>
                  </div>
                  {pairPlayer1Type === 'registered' ? (
                    <div>
                      <input
                        type="text"
                        value={pairSearchQuery1}
                        onChange={(e) => setPairSearchQuery1(e.target.value)}
                        placeholder="חפש שם שחקן..."
                        className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
                      />
                      <div className="max-h-36 overflow-y-auto border rounded-lg">
                        {allUsers
                          .filter(u => !tournament?.registered_players?.includes(u.id))
                          .filter(u => pairSearchQuery1 === '' || u.full_name.toLowerCase().includes(pairSearchQuery1.toLowerCase()))
                          .slice(0, 10)
                          .map(u => (
                            <button
                              key={u.id}
                              onClick={() => setPairPlayer1Id(u.id)}
                              className={`w-full flex items-center gap-2 p-2 text-right hover:bg-blue-50 text-sm ${pairPlayer1Id === u.id ? 'bg-blue-100 border-r-4 border-blue-600' : ''}`}
                            >
                              <span className="flex-1">{u.full_name}</span>
                              {pairPlayer1Id === u.id && <span className="text-green-600 font-bold">&#10003;</span>}
                            </button>
                          ))
                        }
                      </div>
                      {pairPlayer1Id && <p className="text-sm text-green-600 mt-1 font-medium">נבחר: {allUsers.find(u => u.id === pairPlayer1Id)?.full_name}</p>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={pairPlayer1Name}
                        onChange={(e) => setPairPlayer1Name(e.target.value)}
                        placeholder="שם מלא *"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                      <input
                        type="tel"
                        value={pairPlayer1Phone}
                        onChange={(e) => setPairPlayer1Phone(e.target.value)}
                        placeholder="טלפון (אופציונלי)"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* שחקן 2 */}
                <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/50">
                  <h3 className="font-bold text-orange-700 mb-3">שחקן 2 (פרטנר)</h3>
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => { setPairPlayer2Type('registered'); setPairPlayer2Name(''); setPairPlayer2Phone(''); }}
                      className={`flex-1 py-1.5 text-sm rounded-lg font-medium ${pairPlayer2Type === 'registered' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                    >
                      שחקן רשום
                    </button>
                    <button
                      onClick={() => { setPairPlayer2Type('external'); setPairPlayer2Id(''); }}
                      className={`flex-1 py-1.5 text-sm rounded-lg font-medium ${pairPlayer2Type === 'external' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                    >
                      שחקן חיצוני
                    </button>
                  </div>
                  {pairPlayer2Type === 'registered' ? (
                    <div>
                      <input
                        type="text"
                        value={pairSearchQuery2}
                        onChange={(e) => setPairSearchQuery2(e.target.value)}
                        placeholder="חפש שם שחקן..."
                        className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
                      />
                      <div className="max-h-36 overflow-y-auto border rounded-lg">
                        {allUsers
                          .filter(u => !tournament?.registered_players?.includes(u.id) && u.id !== pairPlayer1Id)
                          .filter(u => pairSearchQuery2 === '' || u.full_name.toLowerCase().includes(pairSearchQuery2.toLowerCase()))
                          .slice(0, 10)
                          .map(u => (
                            <button
                              key={u.id}
                              onClick={() => setPairPlayer2Id(u.id)}
                              className={`w-full flex items-center gap-2 p-2 text-right hover:bg-orange-50 text-sm ${pairPlayer2Id === u.id ? 'bg-orange-100 border-r-4 border-orange-600' : ''}`}
                            >
                              <span className="flex-1">{u.full_name}</span>
                              {pairPlayer2Id === u.id && <span className="text-green-600 font-bold">&#10003;</span>}
                            </button>
                          ))
                        }
                      </div>
                      {pairPlayer2Id && <p className="text-sm text-green-600 mt-1 font-medium">נבחר: {allUsers.find(u => u.id === pairPlayer2Id)?.full_name}</p>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={pairPlayer2Name}
                        onChange={(e) => setPairPlayer2Name(e.target.value)}
                        placeholder="שם מלא *"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                      <input
                        type="tel"
                        value={pairPlayer2Phone}
                        onChange={(e) => setPairPlayer2Phone(e.target.value)}
                        placeholder="טלפון (אופציונלי)"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* כפתור הוספת הזוג */}
                <Button
                  onClick={addPairToTournament}
                  disabled={addingPlayer}
                  className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-bold py-3"
                  data-testid="add-pair-btn"
                >
                  {addingPlayer ? (
                    <><Loader className="w-4 h-4 ml-2 animate-spin" /> מוסיף זוג...</>
                  ) : (
                    'הוסף זוג לתחרות'
                  )}
                </Button>
              </div>
            ) : (
              /* ========== מודל הוספת שחקן יחיד (קיים) ========== */
              <>
                {/* טאבים */}
                <div className="flex border-b mb-4">
                  <button
                    onClick={() => setAddPlayerTab('registered')}
                    className={`flex-1 py-2 text-center font-medium ${
                      addPlayerTab === 'registered' 
                        ? 'border-b-2 border-blue-600 text-blue-600' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    שחקן רשום
                  </button>
                  <button
                    onClick={() => setAddPlayerTab('external')}
                    className={`flex-1 py-2 text-center font-medium ${
                      addPlayerTab === 'external' 
                        ? 'border-b-2 border-green-600 text-green-600' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    שחקן חיצוני
                  </button>
                </div>
                
                {/* תוכן לפי טאב */}
                {addPlayerTab === 'registered' ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">חפש שחקן מרשימת המשתמשים הרשומים באתר:</p>
                    
                    <input
                      type="text"
                      value={playerSearchQuery}
                      onChange={(e) => setPlayerSearchQuery(e.target.value)}
                      placeholder="הקלד שם לחיפוש..."
                      className="w-full border-2 border-blue-300 rounded-lg px-4 py-3 text-lg focus:border-blue-500 focus:outline-none"
                      autoFocus
                    />
                    
                    <div className="max-h-64 overflow-y-auto border rounded-lg">
                      {loadingUsers ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader className="w-6 h-6 animate-spin text-blue-600" />
                          <span className="mr-2 text-gray-600">טוען שחקנים...</span>
                        </div>
                      ) : allUsers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          לא נמצאו משתמשים
                        </div>
                      ) : (
                        <div className="divide-y">
                          {allUsers
                            .filter(u => !tournament?.registered_players?.includes(u.id))
                            .filter(u => 
                              playerSearchQuery === '' || 
                              u.full_name.toLowerCase().includes(playerSearchQuery.toLowerCase()) ||
                              (u.phone && u.phone.includes(playerSearchQuery))
                            )
                            .sort((a, b) => a.full_name.localeCompare(b.full_name, 'he'))
                            .slice(0, 20)
                            .map(u => (
                              <button
                                key={u.id}
                                onClick={() => setSelectedPlayerToAdd(u.id)}
                                className={`w-full flex items-center gap-3 p-3 text-right hover:bg-blue-50 transition-colors ${
                                  selectedPlayerToAdd === u.id ? 'bg-blue-100 border-r-4 border-blue-600' : ''
                                }`}
                              >
                                <Avatar className="w-10 h-10">
                                  {u.avatar && <AvatarImage src={u.avatar} alt={u.full_name} />}
                                  <AvatarFallback className="bg-blue-600 text-white">
                                    {u.full_name.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="font-semibold">{u.full_name}</div>
                                  <div className="text-sm text-gray-500 flex gap-2">
                                    {u.total_points && <span>{u.total_points} נק</span>}
                                    {u.rank && <span>#{u.rank}</span>}
                                  </div>
                                </div>
                                {selectedPlayerToAdd === u.id && (
                                  <div className="text-green-600 font-bold">&#10003;</div>
                                )}
                              </button>
                            ))
                          }
                          {allUsers
                            .filter(u => !tournament?.registered_players?.includes(u.id))
                            .filter(u => 
                              playerSearchQuery === '' || 
                              u.full_name.toLowerCase().includes(playerSearchQuery.toLowerCase()) ||
                              (u.phone && u.phone.includes(playerSearchQuery))
                            ).length === 0 && (
                            <div className="text-center py-4 text-gray-500">
                              לא נמצאו שחקנים עם: {playerSearchQuery}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={addRegisteredPlayer}
                      disabled={!selectedPlayerToAdd || addingPlayer}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {addingPlayer ? (
                        <><Loader className="w-4 h-4 ml-2 animate-spin" /> מוסיף...</>
                      ) : selectedPlayerToAdd ? (
                        `הוסף את ${allUsers.find(u => u.id === selectedPlayerToAdd)?.full_name || 'השחקן'}`
                      ) : (
                        'בחר שחקן מהרשימה'
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">הוסף שחקן שלא רשום באתר:</p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">שם מלא *</label>
                      <input
                        type="text"
                        value={externalPlayerName}
                        onChange={(e) => setExternalPlayerName(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="שם השחקן"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">טלפון (אופציונלי)</label>
                      <input
                        type="tel"
                        value={externalPlayerPhone}
                        onChange={(e) => setExternalPlayerPhone(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="050-1234567"
                      />
                    </div>
                    <Button
                      onClick={addExternalPlayer}
                      disabled={!externalPlayerName.trim() || addingPlayer}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {addingPlayer ? (
                        <><Loader className="w-4 h-4 ml-2 animate-spin" /> מוסיף...</>
                      ) : (
                        'הוסף שחקן חיצוני'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
            
            <Button
              onClick={() => {
                setIsAddPlayerModalOpen(false);
                setSelectedPlayerToAdd('');
                setExternalPlayerName('');
                setExternalPlayerPhone('');
                setPlayerSearchQuery('');
              }}
              variant="outline"
              className="w-full mt-4"
            >
              ביטול
            </Button>
          </div>
        </div>
      )}

      {/* Modal עריכת פרטי תחרות */}
      {isEditTournamentModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">✏️ עריכת פרטי תחרות</h2>
              <button
                onClick={() => setIsEditTournamentModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* שם התחרות */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">שם התחרות</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* סוג תחרות */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">סוג תחרות</label>
                <select
                  value={`${editFormData.type}_${editFormData.max_players}`}
                  onChange={(e) => {
                    const [newType, maxStr] = e.target.value.split('_');
                    setEditFormData({...editFormData, type: newType, max_players: parseInt(maxStr)});
                  }}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="daily_8">יומית (8 שחקנים)</option>
                  <option value="daily_16">יומית (16 שחקנים)</option>
                  <option value="knockout_32">נוקאאוט (32 שחקנים)</option>
                </select>
              </div>

              {/* קטגוריה */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">קטגוריה</label>
                <select
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="דרג א">דרג א</option>
                  <option value="דרג ב">דרג ב</option>
                  <option value="דרג ג">דרג ג</option>
                  <option value="יחידים open">יחידים open</option>
                  <option value="זוגות open">זוגות open</option>
                  <option value="נשים">נשים</option>
                </select>
              </div>
              
              {/* תאריך */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">תאריך</label>
                <input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              {/* שעת התחלה */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">שעת התחלה</label>
                <input
                  type="time"
                  value={editFormData.start_time}
                  onChange={(e) => setEditFormData({...editFormData, start_time: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              {/* מיקום */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">מיקום</label>
                <input
                  type="text"
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              {/* מספר שחקנים מקסימלי */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">מספר שחקנים מקסימלי</label>
                <input
                  type="number"
                  value={editFormData.max_players}
                  onChange={(e) => setEditFormData({...editFormData, max_players: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              {/* קישור וואטסאפ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">קישור וואטסאפ</label>
                <input
                  type="url"
                  value={editFormData.whatsapp_link}
                  onChange={(e) => setEditFormData({...editFormData, whatsapp_link: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="https://chat.whatsapp.com/..."
                />
              </div>
              
              {/* תיאור */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">תיאור התחרות</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 h-24"
                  placeholder="הוסף תיאור לתחרות..."
                />
              </div>
              
              {/* פרסים */}
              <div className="border-t pt-4">
                <h3 className="font-bold text-lg mb-3">🏆 פרסים</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">מקום ראשון</label>
                    <input
                      type="text"
                      value={editFormData.prizes.first_place}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        prizes: {...editFormData.prizes, first_place: e.target.value}
                      })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">מקום שני</label>
                    <input
                      type="text"
                      value={editFormData.prizes.second_place}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        prizes: {...editFormData.prizes, second_place: e.target.value}
                      })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">מקום שלישי</label>
                    <input
                      type="text"
                      value={editFormData.prizes.third_place}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        prizes: {...editFormData.prizes, third_place: e.target.value}
                      })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  
                  {tournament?.type === 'knockout' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">מקום 1 תנחומים</label>
                      <input
                        type="text"
                        value={editFormData.prizes.consolation_winner}
                        onChange={(e) => setEditFormData({
                          ...editFormData, 
                          prizes: {...editFormData.prizes, consolation_winner: e.target.value}
                        })}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* כפתורי שמירה/ביטול */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={saveEditTournament}
                disabled={savingEdit}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {savingEdit ? (
                  <>
                    <Loader className="w-4 h-4 ml-2 animate-spin" />
                    שומר...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 ml-2" />
                    שמור שינויים
                  </>
                )}
              </Button>
              <Button
                onClick={() => setIsEditTournamentModalOpen(false)}
                variant="outline"
                className="flex-1"
              >
                ביטול
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal עריכת שיטת התחרות */}
      {isEditRulesModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-purple-700">📋 עריכת שיטת התחרות</h2>
              <button
                onClick={() => setIsEditRulesModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  💡 <strong>טיפ:</strong> כתוב את כל פרטי שיטת התחרות והמשחקים כאן. הטקסט יוצג בדיוק כפי שתכתוב אותו.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">שיטת התחרות והמשחקים</label>
                <textarea
                  value={rulesText}
                  onChange={(e) => setRulesText(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 h-80 text-right focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                  placeholder={tournament?.type === 'daily' ? 
`📋 שיטת התחרות והמשחקים:
• שלב בתים (4 בתים של 4 שחקנים)
• משחקים מ-2:2 עד סט מלא
• 2 עולים מכל בית לשלב נוקאאוט

(ייתכנו שינויים בהתאם לזמנים)` : 
`✨ פרטי התחרות:
שיטת משחקים: הטוב מ-3 מערכות, הסט השלישי הוא סופר טייבריק
תנחומים: מפסידים בסיבוב הראשון בלבד יורדים לטורניר תנחומים

🗓️ שיטת התחרות:
• התחרות תימשך כחודש וחצי
• השחקנים אחראים לקבוע ביניהם מיקום ושעה
• מי שלא יופיע – מפסיד טכני!`}
                  dir="rtl"
                />
              </div>
            </div>
            
            {/* כפתורי שמירה/ביטול */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={saveRulesText}
                disabled={savingRules}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {savingRules ? (
                  <>
                    <Loader className="w-4 h-4 ml-2 animate-spin" />
                    שומר...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 ml-2" />
                    שמור שיטת התחרות
                  </>
                )}
              </Button>
              <Button
                onClick={() => setIsEditRulesModalOpen(false)}
                variant="outline"
                className="flex-1"
              >
                ביטול
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Prizes Edit Modal */}
      {isPrizesModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full" dir="rtl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-yellow-700">🏆 עריכת פרסי התחרות</h2>
              <button onClick={() => setIsPrizesModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-yellow-700 mb-1">🥇 מקום ראשון</label>
                <input
                  type="text"
                  value={prizesForm.first_place}
                  onChange={(e) => setPrizesForm({...prizesForm, first_place: e.target.value})}
                  className="w-full px-4 py-2.5 border-2 border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-yellow-50"
                  placeholder="לדוגמה: 500 ₪ + גביע"
                  data-testid="prize-first-place"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-600 mb-1">🥈 מקום שני</label>
                <input
                  type="text"
                  value={prizesForm.second_place}
                  onChange={(e) => setPrizesForm({...prizesForm, second_place: e.target.value})}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-gray-50"
                  placeholder="לדוגמה: 250 ₪"
                  data-testid="prize-second-place"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-orange-600 mb-1">🥉 מקום שלישי</label>
                <input
                  type="text"
                  value={prizesForm.third_place}
                  onChange={(e) => setPrizesForm({...prizesForm, third_place: e.target.value})}
                  className="w-full px-4 py-2.5 border-2 border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-orange-50"
                  placeholder="לדוגמה: 100 ₪"
                  data-testid="prize-third-place"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-blue-600 mb-1">🏅 טורניר תנחומים</label>
                <input
                  type="text"
                  value={prizesForm.consolation_winner}
                  onChange={(e) => setPrizesForm({...prizesForm, consolation_winner: e.target.value})}
                  className="w-full px-4 py-2.5 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-blue-50"
                  placeholder="לדוגמה: מדליה"
                  data-testid="prize-consolation"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={savePrizes}
                disabled={savingPrizes}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                data-testid="save-prizes-btn"
              >
                {savingPrizes ? 'שומר...' : '💾 שמור פרסים'}
              </Button>
              <Button onClick={() => setIsPrizesModalOpen(false)} variant="outline" className="flex-1">
                ביטול
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: אישור ביטול הרשמה + אזהרת דמי ביטול */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent dir="rtl" data-testid="cancel-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-right text-red-600">
              ⚠️ ביטול הרשמה לתחרות
            </DialogTitle>
            <DialogDescription className="text-right">
              {(() => {
                const { fee, hours } = getEstimatedCancelFee();
                if (fee > 0) {
                  return (
                    <span className="block space-y-2">
                      <span className="block text-gray-700 dark:text-gray-300">
                        נותרו כ-{hours} שעות עד תחילת התחרות. בהתאם למדיניות הביטולים, ביטול ההרשמה כעת יחייב אותך בדמי ביטול:
                      </span>
                      <span className="block text-2xl font-extrabold text-red-600" data-testid="cancel-estimated-fee">{fee}₪</span>
                      <span className="block text-sm text-red-500">
                        אי-תשלום דמי הביטול יגרום להשהיית חשבונך ולחסימת הרשמה לתחרויות עתידיות.
                      </span>
                    </span>
                  );
                }
                return (
                  <span className="block text-emerald-600 font-medium" data-testid="cancel-estimated-fee">
                    הביטול חינם — נותר מספיק זמן עד התחרות (ללא דמי ביטול).
                  </span>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} data-testid="cancel-dialog-back-btn">
              חזרה
            </Button>
            <Button variant="destructive" onClick={handleUnregister} data-testid="cancel-dialog-confirm-btn">
              אישור ביטול ההרשמה
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog: חלוקת נקודות אוטומטית לפי דירוג */}
      <Dialog open={showPointsDialog} onOpenChange={setShowPointsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl" data-testid="award-points-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-right">
              <Coins className="w-6 h-6 text-amber-500" />
              חלוקת נקודות לפי דירוג ({pointsPreview?.tournament_type === 'daily' ? 'יומי' : 'נוקאאוט'})
            </DialogTitle>
            <DialogDescription className="sr-only">חלוקת נקודות אוטומטית לשחקני התחרות לפי המיקום הסופי</DialogDescription>
          </DialogHeader>

          {pointsPreview?.points_awarded && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800" data-testid="already-awarded-warning">
              ⚠️ כבר חולקו נקודות לתחרות זו. חלוקה מחדש תבטל את ההענקה הקודמת ותחיל את הערכים החדשים (ללא ספירה כפולה).
            </div>
          )}

          <p className="text-sm text-gray-500 text-right">
            הנקודות חושבו אוטומטית לפי המיקום הסופי של כל שחקן בתחרות. ניתן לערוך לפני אישור.
          </p>

          <div className="space-y-2">
            {pointsPreview?.players?.length === 0 && (
              <p className="text-center text-gray-500 py-4">אין שחקנים זכאים לנקודות עדיין (ודא שהתחרות הסתיימה / שחקנים חיצוניים מדולגים).</p>
            )}
            {pointsPreview?.players?.map((p) => (
              <div key={p.user_id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`award-row-${p.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="text-xs text-gray-500">
                    {p.position_label} · נוכחי: {p.current_total_points} נק׳
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Input
                    type="number"
                    min="0"
                    value={awardEdits[p.user_id] ?? 0}
                    onChange={(e) => setAwardEdits(prev => ({ ...prev, [p.user_id]: e.target.value }))}
                    className="w-24 text-center"
                    data-testid={`award-input-${p.user_id}`}
                  />
                  <span className="text-sm text-gray-500">נק׳</span>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowPointsDialog(false)} data-testid="award-cancel-btn">
              ביטול
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleAwardPoints}
              disabled={awarding || !pointsPreview?.players?.length}
              data-testid="award-confirm-btn"
            >
              {awarding ? 'מחלק...' : 'אשר וחלק נקודות'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentDetailsPage;
